// src/pages/consolidated/ConsolidatedDashboard.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import Header from "../../layouts/Header";
import Footer from "../../layouts/Footer";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import {
  Button,
  Card,
  Col,
  Row,
  Table,
  Badge,
  ProgressBar,
  ButtonGroup,
  Spinner,
  Dropdown,
  Container,
  Alert,
  Placeholder,
  Form,
  Modal,
} from "react-bootstrap";
import ReactApexChart from "react-apexcharts";
import axios from "axios";
import {
  People,
  Clock,
  Receipt,
  Calendar,
  ArrowUp,
  ArrowDown,
  Cash,
  Eye,
  CheckCircle,
  Activity,
  BarChart,
  PieChart,
  Globe,
  ExclamationCircle,
  ClockHistory,
  Repeat,
  Shop,
  Download,
  FileEarmarkExcel,
  FilePdf,
  Bullseye,
  GraphUp,
  Award,
  Layers,
  Trash,
  House,
  Info,
  Grid,
  Lightning,
  PersonPlus,
  FileEarmarkBarGraph,
  BoxArrowUpRight,
  XCircle,
} from "react-bootstrap-icons";
import { API_BASE_URL } from "../../constants/api_constants";

export default function ConsolidatedDashboard() {
  const defaultCurrency = sessionStorage.getItem("default_currency") || "XOF";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState("month");
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [selectedMarket, setSelectedMarket] = useState("all");
  const [exportLoading, setExportLoading] = useState(false);

  // ✅ Filtres de dates personnalisées
  const [dateRange, setDateRange] = useState({
    startDate: "",
    endDate: "",
    period: "month",
  });

  // ✅ États pour le modal d'export par marché
  const [showMarketExportModal, setShowMarketExportModal] = useState(false);
  const [selectedMarketForExport, setSelectedMarketForExport] = useState("");
  const [marketExportData, setMarketExportData] = useState(null);
  const [marketExportLoading, setMarketExportLoading] = useState(false);
  const [marketExportError, setMarketExportError] = useState(null);
  const [marketExportPeriod, setMarketExportPeriod] = useState({
    startDate: "",
    endDate: "",
    period: "month",
  });
  const [useMainPeriod, setUseMainPeriod] = useState(true);

  const [dashboardData, setDashboardData] = useState({
    markets: [],
    globalStats: null,
    marketComparison: [],
    taxDistribution: [],
    monthlyEvolution: [],
    recentActivities: [],
    topMarkets: [],
    salubriteStats: null,
    loyerStats: null,
    etalageStats: null,
  });

  const salubriteStats = dashboardData.salubriteStats || {};
  const loyerStats = dashboardData.loyerStats || {};
  const etalageStats = dashboardData.etalageStats || {};

  // Notification Toast
  const showNotification = (message, type = "success") => {
    let toastContainer = document.getElementById("toast-container");
    if (!toastContainer) {
      toastContainer = document.createElement("div");
      toastContainer.id = "toast-container";
      toastContainer.className = "position-fixed top-0 end-0 p-3";
      toastContainer.style.zIndex = "9999";
      toastContainer.style.maxWidth = "450px";
      document.body.appendChild(toastContainer);
    }

    const configs = {
      success: { icon: "✅", title: "Succès" },
      danger: { icon: "❌", title: "Erreur" },
      warning: { icon: "⚠️", title: "Attention" },
      info: { icon: "ℹ️", title: "Information" },
    };

    const config = configs[type] || configs.info;
    const toast = document.createElement("div");
    toast.className = `toast align-items-center border-0 show shadow-lg`;
    toast.style.opacity = "0";
    toast.style.transform = "translateX(100%)";
    toast.style.transition = "all 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55)";
    toast.style.background = "white";
    toast.style.borderRadius = "12px";
    toast.style.boxShadow = "0 10px 40px rgba(0,0,0,0.15)";
    toast.style.borderLeft = `5px solid ${
      type === "success" ? "#28a745" : type === "danger" ? "#dc3545" : type === "warning" ? "#ffc107" : "#17a2b8"
    }`;
    toast.style.minWidth = "280px";

    toast.innerHTML = `
      <div class="d-flex align-items-center p-2">
        <div class="flex-shrink-0 ms-2">
          <div style="width: 40px; height: 40px; border-radius: 50%; background: ${
            type === "success" ? "#d4edda" : type === "danger" ? "#f8d7da" : type === "warning" ? "#fff3cd" : "#d1ecf1"
          }; display: flex; align-items: center; justify-content: center; font-size: 20px;">
            ${config.icon}
          </div>
        </div>
        <div class="flex-grow-1 ms-3">
          <div class="fw-bold" style="font-size: 13px; color: #1a1a1a;">${config.title}</div>
          <div style="font-size: 12px; color: #555; line-height: 1.3;">${message}</div>
        </div>
        <button type="button" class="btn-close me-2" aria-label="Fermer" style="font-size: 10px;"></button>
      </div>
    `;

    toastContainer.appendChild(toast);

    setTimeout(() => {
      toast.style.opacity = "1";
      toast.style.transform = "translateX(0)";
    }, 50);

    setTimeout(() => {
      toast.style.opacity = "0";
      toast.style.transform = "translateX(100%)";
      setTimeout(() => { if (toast.parentNode) toast.remove(); }, 500);
    }, 5000);
  };

  const getAuthToken = () => sessionStorage.getItem("authToken");
  const getHeaders = () => {
    const token = getAuthToken();
    const headers = { "Content-Type": "application/json", Accept: "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return headers;
  };

  const formatCurrency = (amount) => {
    const numAmount = parseFloat(amount) || 0;
    return numAmount.toLocaleString("fr-FR", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }) + " " + defaultCurrency;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString("fr-FR", {
        day: "2-digit", month: "2-digit", year: "numeric",
      });
    } catch (e) { return dateString; }
  };

  const formatNumberWithDecimals = (value) => {
    const num = parseFloat(value) || 0;
    const parts = num.toFixed(2).split(".");
    return parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, " ") + "," + (parts[1] || "00");
  };

  const formatDateForAPI = (date) => {
    if (!date) return "";
    return new Date(date).toISOString().split("T")[0];
  };

  const updatePeriod = useCallback((period, setter = setDateRange) => {
    const endDate = new Date();
    const startDate = new Date();

    if (period === "week") startDate.setDate(startDate.getDate() - 7);
    else if (period === "month") startDate.setMonth(startDate.getMonth() - 1);
    else if (period === "year") startDate.setFullYear(startDate.getFullYear() - 1);

    setter({
      startDate: formatDateForAPI(startDate),
      endDate: formatDateForAPI(endDate),
      period,
    });
  }, []);

  const resetDateRange = useCallback(() => {
    updatePeriod("month");
  }, [updatePeriod]);

  const loadDashboardData = useCallback(async (forceRefresh = false) => {
    try {
      if (forceRefresh) setRefreshLoading(true);
      else setLoading(true);
      setError(null);

      const params = {
        time_range: timeRange,
        market_id: selectedMarket !== "all" ? selectedMarket : undefined,
        start_date: dateRange.startDate || undefined,
        end_date: dateRange.endDate || undefined,
      };

      const response = await axios.get(`${API_BASE_URL}/consolidated/dashboard`, {
        headers: getHeaders(),
        params,
      });

      if (response.data.success) {
        applyDashboardData(response.data.data);
      } else {
        setError(response.data.message || "Erreur lors du chargement");
      }
    } catch (err) {
      console.error("Erreur chargement dashboard consolidé:", err);
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
      setRefreshLoading(false);
    }
  }, [timeRange, selectedMarket, dateRange.startDate, dateRange.endDate]);

  const applyDashboardData = (data) => {
    setDashboardData({
      markets: data.markets || [],
      globalStats: data.global_stats || null,
      marketComparison: data.market_comparison || [],
      taxDistribution: data.tax_distribution || [],
      monthlyEvolution: data.monthly_evolution || [],
      recentActivities: data.recent_activities || [],
      topMarkets: data.top_markets || [],
      salubriteStats: data.salubrite_stats || null,
      loyerStats: data.loyer_stats || null,
      etalageStats: data.etalage_stats || null,
    });
  };

  const handleTimeRangeChange = (newRange) => {
    if (newRange === timeRange && dateRange.period === newRange) return;
    setTimeRange(newRange);
    updatePeriod(newRange);
  };

  const goToLastActivity = () => {
    const date = stats.derniereActivite;
    if (!date) return;
    setDateRange({
      startDate: date,
      endDate: date,
      period: "custom",
    });
  };

  // ============================================================
  // MODAL EXPORT PAR MARCHÉ
  // ============================================================
  const openMarketExportModal = () => {
    setSelectedMarketForExport("");
    setMarketExportData(null);
    setMarketExportError(null);
    setUseMainPeriod(true);
    setMarketExportPeriod({
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      period: dateRange.period,
    });
    setShowMarketExportModal(true);
  };

  const closeMarketExportModal = () => {
    setShowMarketExportModal(false);
    setSelectedMarketForExport("");
    setMarketExportData(null);
    setMarketExportError(null);
    setMarketExportLoading(false);
  };

  // ✅ Charge les stats du marché sélectionné
  const loadMarketExportStats = async () => {
    if (!selectedMarketForExport) {
      setMarketExportError("Veuillez sélectionner un marché.");
      return;
    }

    try {
      setMarketExportLoading(true);
      setMarketExportError(null);

      // ✅ Utilise la période du modal (ou celle du dashboard principal)
      const periodToUse = useMainPeriod ? dateRange : marketExportPeriod;

      const params = {
        time_range: periodToUse.period || "month",
        start_date: periodToUse.startDate || undefined,
        end_date: periodToUse.endDate || undefined,
      };

      const response = await axios.get(
        `${API_BASE_URL}/consolidated/market/${selectedMarketForExport}/export-stats`,
        { headers: getHeaders(), params }
      );

      if (response.data.success) {
        setMarketExportData(response.data.data);
      } else {
        setMarketExportError(response.data.message || "Erreur lors du chargement");
      }
    } catch (err) {
      console.error("Erreur chargement stats marché:", err);
      setMarketExportError(err.response?.data?.message || err.message);
    } finally {
      setMarketExportLoading(false);
    }
  };

  // ✅ Change la période du modal
  const handleMarketPeriodChange = (period) => {
    setUseMainPeriod(false);
    updatePeriod(period, setMarketExportPeriod);
    setMarketExportData(null);
  };

  // ✅ Utilise la période du dashboard principal
  const handleUseMainPeriod = () => {
    setUseMainPeriod(true);
    setMarketExportPeriod({
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      period: dateRange.period,
    });
    setMarketExportData(null);
  };

  // ✅ Export PDF du marché sélectionné
  const exportMarketPDF = () => {
    if (!marketExportData) return;
    const { marche, global_stats, salubrite_stats, loyer_stats, etalage_stats, date_range } = marketExportData;

    const fileName = `RAPPORT_${marche.nom_marche.replace(/\s+/g, "_")}_${new Date().toISOString().split("T")[0]}`;
    const originalTitle = document.title;
    document.title = fileName;

    const fmt = (amount) => (parseFloat(amount) || 0).toLocaleString("fr-FR", {
      minimumFractionDigits: 0, maximumFractionDigits: 0,
    }) + " " + defaultCurrency;

    const fmtDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR") : "-";

    const htmlContent = `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <title>${fileName}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Segoe UI', Arial, sans-serif; padding: 5mm; font-size: 9pt; color: #1a1a1a; }
    .header { text-align: center; padding-bottom: 8px; border-bottom: 3px solid #4f46e5; margin-bottom: 15px; }
    .header h1 { font-size: 16pt; font-weight: 700; color: #4f46e5; text-transform: uppercase; letter-spacing: 1.5px; }
    .header .subtitle { font-size: 11pt; color: #333; margin-top: 3px; }
    .header .meta { font-size: 8pt; color: #555; margin-top: 5px; }
    .section { margin-bottom: 12px; }
    .section-title { font-size: 10pt; font-weight: 700; color: #4f46e5; text-transform: uppercase; border-bottom: 1px solid #dee2e6; padding-bottom: 3px; margin-bottom: 8px; letter-spacing: 0.5px; }
    .stats-grid { display: table; width: 100%; border-collapse: collapse; }
    .stats-row { display: table-row; }
    .stat-item { display: table-cell; padding: 5px 10px; border-bottom: 1px solid #f1f3f5; vertical-align: middle; }
    .stat-item .label { font-size: 8pt; color: #6c757d; }
    .stat-item .value { font-size: 10pt; font-weight: 700; text-align: right; }
    .value.green { color: #22c55e; }
    .value.blue { color: #4f46e5; }
    .value.orange { color: #f59e0b; }
    .value.red { color: #ef4444; }
    .value.purple { color: #8b5cf6; }
    table { width: 100%; border-collapse: collapse; font-size: 8pt; margin-top: 2px; }
    table thead th { background: #f8fafc; padding: 5px 8px; text-align: left; font-weight: 700; border-bottom: 2px solid #dee2e6; font-size: 7.5pt; text-transform: uppercase; color: #475569; }
    table tbody td { padding: 4px 8px; border-bottom: 1px solid #f1f3f5; }
    table tbody tr:nth-child(even) { background: #fafbfc; }
    .text-right { text-align: right; }
    .text-center { text-align: center; }
    .fw-bold { font-weight: 700; }
    .footer { margin-top: 20px; padding-top: 8px; border-top: 1px solid #dee2e6; font-size: 7.5pt; color: #6c757d; text-align: center; }
  </style>
</head>
<body>
  <div class="header">
    <h1>RAPPORT MARCHÉ</h1>
    <div class="subtitle"><strong>${marche.nom_marche}</strong></div>
    <div class="meta">
      <span>Code : ${marche.code_marche}</span> |
      <span>Période : ${date_range.label}</span> |
      <span>Généré le ${new Date().toLocaleDateString("fr-FR")}</span>
    </div>
  </div>

  <div class="section">
    <div class="section-title">1. Synthèse des indicateurs</div>
    <div class="stats-grid">
      <div class="stats-row">
        <div class="stat-item"><span class="label">Superficie</span></div>
        <div class="stat-item"><span class="value">${marche.superficie} m²</span></div>
        <div class="stat-item"><span class="label">Contribuables</span></div>
        <div class="stat-item"><span class="value">${global_stats.total_contribuables || 0}</span></div>
      </div>
      <div class="stats-row">
        <div class="stat-item"><span class="label">Enrôlés sur la période</span></div>
        <div class="stat-item"><span class="value blue">+${global_stats.contribuables_enroles_periode || 0}</span></div>
        <div class="stat-item"><span class="label">Actifs (ont payé)</span></div>
        <div class="stat-item"><span class="value orange">${global_stats.contribuables_actifs_periode || 0}</span></div>
      </div>
      <div class="stats-row">
        <div class="stat-item"><span class="label">Total prévu</span></div>
        <div class="stat-item"><span class="value blue">${fmt(global_stats.total_attendu)}</span></div>
        <div class="stat-item"><span class="label">Total collecté</span></div>
        <div class="stat-item"><span class="value green">${fmt(global_stats.total_collecte)}</span></div>
      </div>
      <div class="stats-row">
        <div class="stat-item"><span class="label">Reste à payer</span></div>
        <div class="stat-item"><span class="value red">${fmt((global_stats.total_attendu || 0) - (global_stats.total_collecte || 0))}</span></div>
        <div class="stat-item"><span class="label">Taux de recouvrement</span></div>
        <div class="stat-item"><span class="value ${global_stats.taux_recouvrement >= 80 ? "green" : "orange"}">${global_stats.taux_recouvrement || 0}%</span></div>
      </div>
    </div>
  </div>

  ${salubrite_stats && salubrite_stats.total_attendu > 0 ? `
  <div class="section">
    <div class="section-title">2. ${salubrite_stats.taxe_nom || "Taxe Salubrité"}</div>
    <div class="stats-grid">
      <div class="stats-row">
        <div class="stat-item"><span class="label">Total attendu</span></div>
        <div class="stat-item"><span class="value">${fmt(salubrite_stats.total_attendu)}</span></div>
        <div class="stat-item"><span class="label">Total collecté</span></div>
        <div class="stat-item"><span class="value green">${fmt(salubrite_stats.total_collecte)}</span></div>
      </div>
      <div class="stats-row">
        <div class="stat-item"><span class="label">Taux de recouvrement</span></div>
        <div class="stat-item"><span class="value ${salubrite_stats.taux_recouvrement >= 80 ? "green" : "orange"}">${salubrite_stats.taux_recouvrement}%</span></div>
        <div class="stat-item"><span class="label">Paiements / Contribuables</span></div>
        <div class="stat-item"><span class="value">${salubrite_stats.nombre_paiements} / ${salubrite_stats.nombre_contribuables}</span></div>
      </div>
    </div>
  </div>` : ""}

  ${loyer_stats && loyer_stats.total_attendu > 0 ? `
  <div class="section">
    <div class="section-title">3. ${loyer_stats.taxe_nom || "Taxe Loyers"}</div>
    <div class="stats-grid">
      <div class="stats-row">
        <div class="stat-item"><span class="label">Total attendu</span></div>
        <div class="stat-item"><span class="value">${fmt(loyer_stats.total_attendu)}</span></div>
        <div class="stat-item"><span class="label">Total collecté</span></div>
        <div class="stat-item"><span class="value green">${fmt(loyer_stats.total_collecte)}</span></div>
      </div>
      <div class="stats-row">
        <div class="stat-item"><span class="label">Taux de recouvrement</span></div>
        <div class="stat-item"><span class="value ${loyer_stats.taux_recouvrement >= 80 ? "green" : "orange"}">${loyer_stats.taux_recouvrement}%</span></div>
        <div class="stat-item"><span class="label">Paiements / Contribuables</span></div>
        <div class="stat-item"><span class="value">${loyer_stats.nombre_paiements} / ${loyer_stats.nombre_contribuables}</span></div>
      </div>
    </div>
  </div>` : ""}

  ${etalage_stats && etalage_stats.total_attendu > 0 ? `
  <div class="section">
    <div class="section-title">4. ${etalage_stats.taxe_nom || "Taxe Étalage"}</div>
    <div class="stats-grid">
      <div class="stats-row">
        <div class="stat-item"><span class="label">Total attendu</span></div>
        <div class="stat-item"><span class="value">${fmt(etalage_stats.total_attendu)}</span></div>
        <div class="stat-item"><span class="label">Total collecté</span></div>
        <div class="stat-item"><span class="value green">${fmt(etalage_stats.total_collecte)}</span></div>
      </div>
      <div class="stats-row">
        <div class="stat-item"><span class="label">Taux de recouvrement</span></div>
        <div class="stat-item"><span class="value ${etalage_stats.taux_recouvrement >= 80 ? "green" : "orange"}">${etalage_stats.taux_recouvrement}%</span></div>
        <div class="stat-item"><span class="label">Paiements / Contribuables</span></div>
        <div class="stat-item"><span class="value">${etalage_stats.nombre_paiements} / ${etalage_stats.nombre_contribuables}</span></div>
      </div>
    </div>
  </div>` : ""}

  <div class="footer">
    <div>Généré par SyncTaxe v2.0 - ${fileName}</div>
    <div>${new Date().toLocaleDateString("fr-FR")} • ${new Date().toLocaleTimeString("fr-FR")}</div>
  </div>
</body>
</html>`;

    const iframe = document.createElement("iframe");
    iframe.style.position = "absolute";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "none";
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(htmlContent);
    iframeDoc.close();

    iframe.contentWindow.onload = () => {
      iframe.contentWindow.document.title = fileName;
      iframe.contentWindow.print();
      setTimeout(() => {
        document.title = originalTitle;
        if (document.body.contains(iframe)) document.body.removeChild(iframe);
        showNotification("PDF marché exporté avec succès", "success");
      }, 1500);
    };
  };

  // ✅ Export Excel du marché sélectionné
  const exportMarketExcel = () => {
    if (!marketExportData) return;
    const { marche, global_stats, salubrite_stats, loyer_stats, etalage_stats, date_range, paiements_par_taxe } = marketExportData;

    try {
      const wb = XLSX.utils.book_new();

      const resumeData = [
        ["RAPPORT MARCHÉ"],
        [""],
        ["Marché", marche.nom_marche],
        ["Code", marche.code_marche],
        ["Superficie (m²)", marche.superficie],
        ["Période", date_range.label],
        ["Date de génération", new Date().toLocaleDateString("fr-FR")],
        [""],
        ["INDICATEURS GLOBAUX"],
        ["Indicateur", "Valeur"],
        ["Total contribuables", global_stats.total_contribuables || 0],
        ["Enrôlés sur la période", global_stats.contribuables_enroles_periode || 0],
        ["Actifs sur la période", global_stats.contribuables_actifs_periode || 0],
        ["Total prévu", global_stats.total_attendu || 0],
        ["Total collecté", global_stats.total_collecte || 0],
        ["Reste à payer", (global_stats.total_attendu || 0) - (global_stats.total_collecte || 0)],
        ["Taux de recouvrement (%)", global_stats.taux_recouvrement || 0],
      ];
      const ws1 = XLSX.utils.aoa_to_sheet(resumeData);
      ws1["!cols"] = [{ wch: 30 }, { wch: 35 }];
      XLSX.utils.book_append_sheet(wb, ws1, "Résumé");

      const taxesData = [
        ["DÉTAIL PAR TAXE"],
        [""],
        ["Type", "Nom", "Attendu", "Collecté", "Reste", "Taux (%)", "Paiements", "Contribuables"],
      ];

      if (salubrite_stats && salubrite_stats.total_attendu > 0) {
        taxesData.push([
          "Salubrité",
          salubrite_stats.taxe_nom || "Taxe Salubrité",
          salubrite_stats.total_attendu,
          salubrite_stats.total_collecte,
          (salubrite_stats.total_attendu || 0) - (salubrite_stats.total_collecte || 0),
          salubrite_stats.taux_recouvrement,
          salubrite_stats.nombre_paiements,
          salubrite_stats.nombre_contribuables,
        ]);
      }
      if (loyer_stats && loyer_stats.total_attendu > 0) {
        taxesData.push([
          "Loyers",
          loyer_stats.taxe_nom || "Taxe Loyers",
          loyer_stats.total_attendu,
          loyer_stats.total_collecte,
          (loyer_stats.total_attendu || 0) - (loyer_stats.total_collecte || 0),
          loyer_stats.taux_recouvrement,
          loyer_stats.nombre_paiements,
          loyer_stats.nombre_contribuables,
        ]);
      }
      if (etalage_stats && etalage_stats.total_attendu > 0) {
        taxesData.push([
          "Étalage",
          etalage_stats.taxe_nom || "Taxe Étalage",
          etalage_stats.total_attendu,
          etalage_stats.total_collecte,
          (etalage_stats.total_attendu || 0) - (etalage_stats.total_collecte || 0),
          etalage_stats.taux_recouvrement,
          etalage_stats.nombre_paiements,
          etalage_stats.nombre_contribuables,
        ]);
      }

      const ws2 = XLSX.utils.aoa_to_sheet(taxesData);
      ws2["!cols"] = [
        { wch: 12 }, { wch: 25 }, { wch: 15 }, { wch: 15 },
        { wch: 15 }, { wch: 12 }, { wch: 12 }, { wch: 15 },
      ];
      XLSX.utils.book_append_sheet(wb, ws2, "Détail par taxe");

      if (paiements_par_taxe && paiements_par_taxe.length > 0) {
        const paiementsData = [
          ["PAIEMENTS PAR TAXE"],
          [""],
          ["Taxe", "Contribuables", "Paiements", "Total collecté"],
          ...paiements_par_taxe.map((p) => [
            p.taxe_nom,
            p.nombre_contribuables,
            p.nombre_paiements,
            p.total_collecte,
          ]),
        ];
        const ws3 = XLSX.utils.aoa_to_sheet(paiementsData);
        ws3["!cols"] = [{ wch: 30 }, { wch: 15 }, { wch: 12 }, { wch: 18 }];
        XLSX.utils.book_append_sheet(wb, ws3, "Paiements par taxe");
      }

      const fileName = `RAPPORT_${marche.nom_marche.replace(/\s+/g, "_")}_${new Date().toISOString().split("T")[0]}`;
      XLSX.writeFile(wb, `${fileName}.xlsx`);
      showNotification("Excel marché exporté avec succès", "success");
    } catch (err) {
      console.error("Erreur export Excel marché:", err);
      showNotification("Erreur lors de l'export Excel ❌", "danger");
    }
  };

  // ============================================================
  // EXPORT PDF GLOBAL
  // ============================================================
  const handleExportPDF = () => {
    if (exportLoading) return;
    setExportLoading(true);

    try {
      const fileName = `RAPPORT_CONSOLIDE_${new Date().toISOString().split("T")[0]}`;
      const originalTitle = document.title;
      document.title = fileName;

      const formatCurrencyPDF = (amount) => {
        const num = parseFloat(amount) || 0;
        return num.toLocaleString("fr-FR", {
          minimumFractionDigits: 0, maximumFractionDigits: 0,
        }) + " " + defaultCurrency;
      };

      const formatDatePDF = (dateString) => {
        if (!dateString) return "-";
        try {
          return new Date(dateString).toLocaleDateString("fr-FR", {
            day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit",
          });
        } catch (e) { return dateString; }
      };

      const periodLabel = dateRange.startDate || dateRange.endDate
        ? `${dateRange.startDate || "Début"} → ${dateRange.endDate || "Aujourd'hui"}`
        : timeRange === "week" ? "Cette semaine"
        : timeRange === "month" ? "Ce mois" : "Cette année";

      const totalMarkets = dashboardData.markets.length;
      const totalContribuables = dashboardData.globalStats?.total_contribuables || 0;
      const contribuablesEnrolesPeriode = dashboardData.globalStats?.contribuables_enroles_periode
        || dashboardData.globalStats?.nouveaux_contribuables || 0;
      const contribuablesActifsPeriode = dashboardData.globalStats?.contribuables_actifs_periode || 0;
      const totalCollecte = dashboardData.globalStats?.total_collecte || 0;
      const tauxRecouvrement = dashboardData.globalStats?.taux_recouvrement || 0;

      const htmlContent = `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <title>${fileName}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Segoe UI', Arial, sans-serif; padding: 4mm 5mm; background: white; font-size: 9pt; line-height: 1.25; color: #1a1a1a; }
    .report { max-width: 100%; margin: 0 auto; }
    .header { text-align: center; padding-bottom: 6px; border-bottom: 3px solid #4f46e5; margin-bottom: 12px; position: relative; }
    .header .top-line { font-size: 7pt; letter-spacing: 3px; text-transform: uppercase; color: #6c757d; margin-bottom: 2px; }
    .header h1 { font-size: 16pt; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: #4f46e5; margin: 2px 0; }
    .header .subtitle { font-size: 11pt; font-weight: 400; color: #2a2a2a; margin-top: 1px; }
    .header .meta { font-size: 7.5pt; color: #555; margin-top: 3px; display: flex; justify-content: center; gap: 20px; }
    .header .ref { position: absolute; right: 0; bottom: 4px; font-size: 6.5pt; color: #adb5bd; font-style: italic; }
    .section { margin-bottom: 10px; }
    .section-title { font-size: 10pt; font-weight: 700; text-transform: uppercase; border-bottom: 1px solid #dee2e6; padding-bottom: 3px; margin-bottom: 6px; color: #4f46e5; letter-spacing: 0.5px; }
    .stats-grid { display: table; width: 100%; border-collapse: collapse; }
    .stats-row { display: table-row; }
    .stat-item { display: table-cell; padding: 4px 8px; border-bottom: 1px solid #f1f3f5; vertical-align: middle; }
    .stat-item .label { font-size: 8pt; color: #6c757d; }
    .stat-item .value { font-size: 10pt; font-weight: 700; text-align: right; }
    .stat-item .value.green { color: #22c55e; }
    .stat-item .value.blue { color: #4f46e5; }
    .stat-item .value.orange { color: #f59e0b; }
    table { width: 100%; border-collapse: collapse; font-size: 8pt; margin-top: 2px; }
    table thead th { background: #f8fafc; padding: 4px 6px; text-align: left; font-weight: 700; border-bottom: 2px solid #dee2e6; font-size: 7pt; text-transform: uppercase; color: #475569; }
    table tbody td { padding: 3px 6px; border-bottom: 1px solid #f1f3f5; font-size: 8pt; }
    table tbody tr:nth-child(even) { background: #fafbfc; }
    .text-right { text-align: right; }
    .text-center { text-align: center; }
    .fw-bold { font-weight: 700; }
    .badge { display: inline-block; padding: 1px 8px; border-radius: 12px; font-size: 7.5pt; font-weight: 600; }
    .badge.success { background: #e8f5e9; color: #22c55e; }
    .badge.warning { background: #fff3e0; color: #f59e0b; }
    .badge.danger { background: #ffebee; color: #ef4444; }
    .footer { margin-top: 14px; padding-top: 6px; border-top: 1px solid #dee2e6; font-size: 7pt; color: #6c757d; display: flex; justify-content: space-between; }
    @media print { body { padding: 3mm 4mm; } }
  </style>
</head>
<body>
  <div class="report">
    <div class="header">
      <div class="top-line">● ● ● RAPPORT CONSOLIDÉ ● ● ●</div>
      <h1>TABLEAU DE BORD CONSOLIDÉ</h1>
      <div class="subtitle">VUE D'ENSEMBLE DES MARCHÉS</div>
      <div class="meta">
        <span>Période : ${periodLabel}</span>
        <span>|</span>
        <span>${new Date().toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" })}</span>
        <span>|</span>
        <span>${totalMarkets} marchés</span>
      </div>
      <div class="ref">REF: ${fileName}</div>
    </div>

    <div class="section">
      <div class="section-title">1. Synthèse des indicateurs</div>
      <div class="stats-grid">
        <div class="stats-row">
          <div class="stat-item"><span class="label">Total Marchés</span></div>
          <div class="stat-item"><span class="value">${totalMarkets}</span></div>
          <div class="stat-item"><span class="label">Contribuables (stock total)</span></div>
          <div class="stat-item"><span class="value">${totalContribuables}</span></div>
        </div>
        <div class="stats-row">
          <div class="stat-item"><span class="label">Enrôlés sur la période</span></div>
          <div class="stat-item"><span class="value blue">+${contribuablesEnrolesPeriode}</span></div>
          <div class="stat-item"><span class="label">Actifs sur la période</span></div>
          <div class="stat-item"><span class="value orange">${contribuablesActifsPeriode}</span></div>
        </div>
        <div class="stats-row">
          <div class="stat-item"><span class="label">Collectes Totales</span></div>
          <div class="stat-item"><span class="value green">${formatCurrencyPDF(totalCollecte)}</span></div>
          <div class="stat-item"><span class="label">Taux de Recouvrement</span></div>
          <div class="stat-item"><span class="value ${tauxRecouvrement >= 80 ? "green" : "orange"}">${tauxRecouvrement}%</span></div>
        </div>
      </div>
    </div>

    <div class="section">
      <div class="section-title">2. Performance des marchés</div>
      <table>
        <thead>
          <tr>
            <th>#</th>
            <th>Marché</th>
            <th class="text-right">Contribuables</th>
            <th class="text-right">Enrôlés période</th>
            <th class="text-right">Collecté</th>
            <th class="text-right">Objectif</th>
            <th class="text-right">Performance</th>
          </tr>
        </thead>
        <tbody>
          ${dashboardData.marketComparison.map((market, idx) => `
            <tr>
              <td>${idx + 1}</td>
              <td><strong>${market.nom}</strong></td>
              <td class="text-right">${market.total_contribuables || market.nombre_commercants || 0}</td>
              <td class="text-right">+${market.contribuables_enroles_periode || 0}</td>
              <td class="text-right fw-bold">${formatCurrencyPDF(market.collecte)}</td>
              <td class="text-right">${formatCurrencyPDF(market.objectif)}</td>
              <td class="text-right">
                <span class="badge ${market.performance >= 80 ? "success" : market.performance >= 50 ? "warning" : "danger"}">
                  ${market.performance}%
                </span>
              </td>
            </tr>
          `).join("")}
        </tbody>
      </table>
    </div>

    ${dashboardData.salubriteStats && dashboardData.salubriteStats.total_attendu > 0 ? `
    <div class="section">
      <div class="section-title">3. ${dashboardData.salubriteStats.taxe_nom || "Taxe Salubrité"}</div>
      <div class="stats-grid">
        <div class="stats-row">
          <div class="stat-item"><span class="label">Total attendu</span></div>
          <div class="stat-item"><span class="value">${formatCurrencyPDF(dashboardData.salubriteStats.total_attendu)}</span></div>
          <div class="stat-item"><span class="label">Total collecté</span></div>
          <div class="stat-item"><span class="value green">${formatCurrencyPDF(dashboardData.salubriteStats.total_collecte)}</span></div>
        </div>
      </div>
    </div>
    ` : ""}

    ${dashboardData.loyerStats && dashboardData.loyerStats.total_attendu > 0 ? `
    <div class="section">
      <div class="section-title">3bis. ${dashboardData.loyerStats.taxe_nom || "Taxe Loyers"}</div>
      <div class="stats-grid">
        <div class="stats-row">
          <div class="stat-item"><span class="label">Total attendu</span></div>
          <div class="stat-item"><span class="value">${formatCurrencyPDF(dashboardData.loyerStats.total_attendu)}</span></div>
          <div class="stat-item"><span class="label">Total collecté</span></div>
          <div class="stat-item"><span class="value green">${formatCurrencyPDF(dashboardData.loyerStats.total_collecte)}</span></div>
        </div>
      </div>
    </div>
    ` : ""}

    ${dashboardData.etalageStats && dashboardData.etalageStats.total_attendu > 0 ? `
    <div class="section">
      <div class="section-title">3ter. ${dashboardData.etalageStats.taxe_nom || "Taxe Étalage"}</div>
      <div class="stats-grid">
        <div class="stats-row">
          <div class="stat-item"><span class="label">Total attendu</span></div>
          <div class="stat-item"><span class="value">${formatCurrencyPDF(dashboardData.etalageStats.total_attendu)}</span></div>
          <div class="stat-item"><span class="label">Total collecté</span></div>
          <div class="stat-item"><span class="value green">${formatCurrencyPDF(dashboardData.etalageStats.total_collecte)}</span></div>
        </div>
      </div>
    </div>
    ` : ""}

    <div class="footer">
      <span>Généré par SyncTaxe v2.0</span>
      <span>${new Date().toLocaleDateString("fr-FR")} • ${new Date().toLocaleTimeString("fr-FR")}</span>
    </div>
  </div>
</body>
</html>`;

      const iframe = document.createElement("iframe");
      iframe.style.position = "absolute";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "none";
      document.body.appendChild(iframe);

      const iframeDoc = iframe.contentWindow.document;
      iframeDoc.open();
      iframeDoc.write(htmlContent);
      iframeDoc.close();

      iframe.contentWindow.onload = () => {
        iframe.contentWindow.document.title = fileName;
        iframe.contentWindow.print();

        setTimeout(() => {
          document.title = originalTitle;
          if (document.body.contains(iframe)) document.body.removeChild(iframe);
          setExportLoading(false);
          showNotification("PDF exporté avec succès", "success");
        }, 1500);
      };
    } catch (err) {
      console.error("Erreur export PDF:", err);
      showNotification("Erreur lors de l'export PDF ❌", "danger");
      setExportLoading(false);
    }
  };

  // ============================================================
  // EXPORT EXCEL GLOBAL
  // ============================================================
  const handleExportExcel = () => {
    if (exportLoading) return;
    setExportLoading(true);

    try {
      const wb = XLSX.utils.book_new();

      const resumeData = [
        ["RAPPORT CONSOLIDÉ - TABLEAU DE BORD"],
        [""],
        ["Période", dateRange.startDate || dateRange.endDate
          ? `${dateRange.startDate || "Début"} → ${dateRange.endDate || "Aujourd'hui"}`
          : timeRange === "week" ? "Cette semaine" : timeRange === "month" ? "Ce mois" : "Cette année"],
        ["Date de génération", new Date().toLocaleDateString("fr-FR")],
        ["Total Marchés", dashboardData.markets.length],
        ["Contribuables (stock total)", dashboardData.globalStats?.total_contribuables || 0],
        ["Enrôlés sur la période", dashboardData.globalStats?.contribuables_enroles_periode || 0],
        ["Actifs sur la période", dashboardData.globalStats?.contribuables_actifs_periode || 0],
        ["Collectes Totales", dashboardData.globalStats?.total_collecte || 0],
        ["Taux de Recouvrement", (dashboardData.globalStats?.taux_recouvrement || 0) + "%"],
      ];

      const ws1 = XLSX.utils.aoa_to_sheet(resumeData);
      ws1["!cols"] = [{ wch: 35 }, { wch: 30 }];
      XLSX.utils.book_append_sheet(wb, ws1, "Résumé");

      if (dashboardData.marketComparison.length > 0) {
        const marketData = [
          ["PERFORMANCE DES MARCHÉS"],
          [""],
          ["#", "Marché", "Contribuables", "Enrôlés période", "Collecté", "Objectif", "Performance (%)", "Statut"],
          ...dashboardData.marketComparison.map((market, idx) => [
            idx + 1,
            market.nom,
            market.total_contribuables || market.nombre_commercants || 0,
            market.contribuables_enroles_periode || 0,
            market.collecte || 0,
            market.objectif || 0,
            market.performance || 0,
            market.performance >= 80 ? " Atteint" : market.performance >= 50 ? " En cours" : " À améliorer",
          ]),
        ];

        const ws2 = XLSX.utils.aoa_to_sheet(marketData);
        ws2["!cols"] = [{ wch: 5 }, { wch: 25 }, { wch: 15 }, { wch: 18 }, { wch: 20 }, { wch: 20 }, { wch: 15 }, { wch: 20 }];
        ws2["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 7 } }];
        XLSX.utils.book_append_sheet(wb, ws2, "Marchés");
      }

      const fileName = `RAPPORT_CONSOLIDE_${new Date().toISOString().split("T")[0]}`;
      XLSX.writeFile(wb, `${fileName}.xlsx`);

      showNotification("Excel exporté avec succès", "success");
    } catch (err) {
      console.error("Erreur export Excel:", err);
      showNotification("Erreur lors de l'export Excel ❌", "danger");
    } finally {
      setExportLoading(false);
    }
  };

  // ✅ Statistiques calculées
  const stats = useMemo(() => {
    const gs = dashboardData.globalStats || {};
    return {
      totalMarkets: dashboardData.markets.length,
      totalContribuables: gs.total_contribuables || 0,
      contribuablesEnrolesPeriode: gs.contribuables_enroles_periode || gs.nouveaux_contribuables || 0,
      nouveauxContribuables: gs.nouveaux_contribuables || 0,
      contribuablesActifsPeriode: gs.contribuables_actifs_periode || 0,
      tauxNouveaux: gs.taux_nouveaux || 0,
      totalCollecte: gs.total_collecte || 0,
      tauxRecouvrement: gs.taux_recouvrement || 0,
      hasData: gs.has_data !== false,
      derniereActivite: gs.derniere_activite || null,
    };
  }, [dashboardData]);

  // ============================================================
  // CHART OPTIONS
  // ============================================================
  const comparisonChartOptions = useMemo(() => ({
    chart: {
      type: "bar", height: 350, toolbar: { show: true }, background: "transparent",
      animations: { enabled: true, easing: "easeinout", speed: 800 },
    },
    colors: ["#4f46e5", "#8b5cf6"],
    plotOptions: {
      bar: { borderRadius: 0, horizontal: false, columnWidth: "45%", dataLabels: { position: "top" } },
    },
    dataLabels: {
      enabled: true, formatter: (val) => val.toLocaleString("fr-FR"), offsetY: -20,
      style: { fontSize: "10px", fontWeight: "600", colors: ["#1e293b"] },
    },
    xaxis: {
      categories: dashboardData.marketComparison.map((m) => m.nom) || [],
      labels: { style: { colors: "#475569", fontSize: "12px", fontWeight: "500" }, rotate: -15 },
    },
    yaxis: {
      labels: { formatter: (value) => value.toLocaleString("fr-FR"), style: { colors: "#64748b", fontSize: "11px" } },
      title: { text: `Montant (${defaultCurrency})`, style: { fontSize: "12px", fontWeight: "600", color: "#1e293b" } },
    },
    legend: { position: "top", horizontalAlign: "center", fontSize: "12px", fontWeight: "600" },
    tooltip: { y: { formatter: (value) => `${value.toLocaleString("fr-FR")} ${defaultCurrency}` } },
    grid: { borderColor: "#e2e8f0", strokeDashArray: 5, padding: { top: 20 } },
  }), [dashboardData.marketComparison, defaultCurrency]);

  const evolutionChartOptions = useMemo(() => ({
    chart: {
      type: "area", height: 250, toolbar: { show: true }, background: "transparent",
      animations: { enabled: true, easing: "easeinout", speed: 800 },
    },
    colors: ["#4f46e5", "#22c55e"],
    fill: { type: "gradient", gradient: { shadeIntensity: 0.5, opacityFrom: 0.7, opacityTo: 0.1, stops: [0, 100] } },
    dataLabels: { enabled: false },
    stroke: { curve: "smooth", width: 3 },
    xaxis: {
      categories: dashboardData.monthlyEvolution.map((e) => e.mois) || [],
      labels: { style: { colors: "#64748b", fontSize: "11px" } },
    },
    yaxis: {
      labels: { formatter: (value) => value.toLocaleString("fr-FR"), style: { colors: "#64748b", fontSize: "10px" } },
    },
    tooltip: {
      y: { formatter: (value) => `${value.toLocaleString("fr-FR")} ${defaultCurrency}` }, shared: true,
    },
    legend: { position: "top", horizontalAlign: "center", fontSize: "12px", fontWeight: "600" },
    grid: { borderColor: "#e2e8f0", strokeDashArray: 5 },
  }), [dashboardData.monthlyEvolution, defaultCurrency]);

  const taxDistributionOptions = useMemo(() => ({
    chart: {
      type: "donut", height: 280, toolbar: { show: false },
      animations: { enabled: true, easing: "easeinout", speed: 800 },
    },
    labels: dashboardData.taxDistribution.map((t) => t.nom) || [],
    colors: ["#4f46e5", "#22c55e", "#8b5cf6", "#ef4444", "#f59e0b", "#14b8a6", "#475569", "#06b6d4"],
    legend: {
      position: "bottom", fontSize: "12px", fontWeight: "500",
      markers: { width: 12, height: 12, radius: 4 },
      itemMargin: { horizontal: 10, vertical: 5 },
    },
    plotOptions: {
      pie: {
        donut: {
          size: "60%",
          labels: {
            show: true,
            name: { show: true, fontSize: "14px", fontWeight: "600", color: "#1e293b" },
            value: {
              show: true, fontSize: "18px", fontWeight: "700", color: "#1e293b",
              formatter: (val) => formatCurrency(val),
            },
            total: {
              show: true, label: "Total", fontSize: "13px", fontWeight: "600", color: "#1e293b",
              formatter: () => formatCurrency(dashboardData.taxDistribution.reduce((sum, t) => sum + t.montant, 0)),
            },
          },
        },
      },
    },
    dataLabels: {
      enabled: true, formatter: (val) => `${val.toFixed(1)}%`,
      style: { fontSize: "10px", fontWeight: "500", colors: ["#1e293b"] },
    },
    tooltip: { y: { formatter: (value) => formatCurrency(value) } },
  }), [dashboardData.taxDistribution]);

  const salubriteEvolutionOptions = useMemo(() => ({
    chart: {
      type: "area", height: 180, toolbar: { show: false }, background: "transparent",
      animations: { enabled: true, easing: "easeinout", speed: 800 },
    },
    colors: ["#ef4444"],
    fill: { type: "gradient", gradient: { shadeIntensity: 0.5, opacityFrom: 0.7, opacityTo: 0.1, stops: [0, 100] } },
    dataLabels: { enabled: false },
    stroke: { curve: "smooth", width: 2 },
    xaxis: {
      categories: dashboardData.salubriteStats?.evolution?.map((e) => e.mois) || [],
      labels: { style: { colors: "#64748b", fontSize: "10px" } },
    },
    yaxis: {
      labels: { formatter: (value) => value.toLocaleString("fr-FR"), style: { colors: "#64748b", fontSize: "10px" } },
    },
    tooltip: { y: { formatter: (value) => `${value.toLocaleString("fr-FR")} ${defaultCurrency}` } },
    legend: { show: false },
    grid: { borderColor: "#e2e8f0", strokeDashArray: 5 },
  }), [dashboardData.salubriteStats, defaultCurrency]);

  const salubriteSeries = useMemo(() => [{
    name: "Collecté",
    data: dashboardData.salubriteStats?.evolution?.map((e) => e.collecte) || [],
  }], [dashboardData.salubriteStats]);

  const loyerEvolutionOptions = useMemo(() => ({
    chart: {
      type: "area", height: 180, toolbar: { show: false }, background: "transparent",
      animations: { enabled: true, easing: "easeinout", speed: 800 },
    },
    colors: ["#0d6efd"],
    fill: { type: "gradient", gradient: { shadeIntensity: 0.5, opacityFrom: 0.7, opacityTo: 0.1, stops: [0, 100] } },
    dataLabels: { enabled: false },
    stroke: { curve: "smooth", width: 2 },
    xaxis: {
      categories: dashboardData.loyerStats?.evolution?.map((e) => e.mois) || [],
      labels: { style: { colors: "#64748b", fontSize: "10px" } },
    },
    yaxis: {
      labels: { formatter: (value) => value.toLocaleString("fr-FR"), style: { colors: "#64748b", fontSize: "10px" } },
    },
    tooltip: { y: { formatter: (value) => `${value.toLocaleString("fr-FR")} ${defaultCurrency}` } },
    legend: { show: false },
    grid: { borderColor: "#e2e8f0", strokeDashArray: 5 },
  }), [dashboardData.loyerStats, defaultCurrency]);

  const loyerSeries = useMemo(() => [{
    name: "Collecté",
    data: dashboardData.loyerStats?.evolution?.map((e) => e.collecte) || [],
  }], [dashboardData.loyerStats]);

  const etalageEvolutionOptions = useMemo(() => ({
    chart: {
      type: "area", height: 180, toolbar: { show: false }, background: "transparent",
      animations: { enabled: true, easing: "easeinout", speed: 800 },
    },
    colors: ["#8b5cf6"],
    fill: { type: "gradient", gradient: { shadeIntensity: 0.5, opacityFrom: 0.7, opacityTo: 0.1, stops: [0, 100] } },
    dataLabels: { enabled: false },
    stroke: { curve: "smooth", width: 2 },
    xaxis: {
      categories: dashboardData.etalageStats?.evolution?.map((e) => e.mois) || [],
      labels: { style: { colors: "#64748b", fontSize: "10px" } },
    },
    yaxis: {
      labels: { formatter: (value) => value.toLocaleString("fr-FR"), style: { colors: "#64748b", fontSize: "10px" } },
    },
    tooltip: { y: { formatter: (value) => `${value.toLocaleString("fr-FR")} ${defaultCurrency}` } },
    legend: { show: false },
    grid: { borderColor: "#e2e8f0", strokeDashArray: 5 },
  }), [dashboardData.etalageStats, defaultCurrency]);

  const etalageSeries = useMemo(() => [{
    name: "Collecté",
    data: dashboardData.etalageStats?.evolution?.map((e) => e.collecte) || [],
  }], [dashboardData.etalageStats]);

  // ============================================================
  // COMPOSANTS RÉUTILISABLES
  // ============================================================
  const SkeletonStatCard = () => (
    <Card className="border-0 shadow-sm h-100" style={{ borderRadius: "16px" }}>
      <div className="position-absolute top-0 start-0 w-100" style={{ height: "4px", background: "linear-gradient(90deg, #e2e8f0, #f1f5f9)" }} />
      <Card.Body className="p-4">
        <div className="d-flex align-items-center">
          <div className="rounded-3 p-2 me-3" style={{ backgroundColor: "rgba(79, 70, 229, 0.08)" }}>
            <div className="rounded-circle" style={{ width: "24px", height: "24px", backgroundColor: "#e2e8f0" }} />
          </div>
          <div className="flex-grow-1">
            <Placeholder as="div" animation="glow">
              <Placeholder xs={8} style={{ height: "28px" }} className="mb-1" />
              <Placeholder xs={6} style={{ height: "14px" }} />
            </Placeholder>
          </div>
        </div>
      </Card.Body>
    </Card>
  );

  const StatCard = ({ title, value, icon: Icon, color, subtitle, trend, trendValue, loading, secondaryValue, secondaryLabel }) => {
    const colorMap = {
      primary: { bg: "rgba(79, 70, 229, 0.08)", text: "#4f46e5" },
      info: { bg: "rgba(6, 182, 212, 0.08)", text: "#06b6d4" },
      success: { bg: "rgba(34, 197, 94, 0.08)", text: "#22c55e" },
      warning: { bg: "rgba(245, 158, 11, 0.08)", text: "#f59e0b" },
      danger: { bg: "rgba(239, 68, 68, 0.08)", text: "#ef4444" },
    };

    const colors = colorMap[color] || colorMap.primary;

    return (
      <Card className="border-0 shadow-sm h-100 transition-card" style={{ borderRadius: "16px" }}>
        <div className="position-absolute top-0 start-0 w-100"
          style={{ height: "4px", background: colors.text, borderRadius: "16px 16px 0 0" }} />
        <Card.Body className="p-4">
          <div className="d-flex justify-content-between align-items-start">
            <div className="d-flex align-items-center">
              <div className="rounded-3 p-2 me-3" style={{ backgroundColor: colors.bg }}>
                <Icon size={22} color={colors.text} />
              </div>
              <div>
                {loading ? (
                  <Placeholder as="div" animation="glow">
                    <Placeholder xs={8} style={{ height: "28px" }} className="mb-1" />
                    <Placeholder xs={6} style={{ height: "14px" }} />
                  </Placeholder>
                ) : (
                  <>
                    <h4 className="fw-bold mb-0" style={{ fontSize: "22px", color: "#0f172a" }}>
                      {typeof value === "number" ? value.toLocaleString("fr-FR") : value}
                    </h4>
                    <span className="text-muted small fw-medium" style={{ fontSize: "12px" }}>{title}</span>
                  </>
                )}
              </div>
            </div>
            {trend && !loading && (
              <div className={`d-flex align-items-center px-2 py-1 rounded-pill ${trend === "up" ? "bg-success-subtle text-success" : "bg-danger-subtle text-danger"}`}
                style={{ fontSize: "11px", fontWeight: "600" }}>
                {trend === "up" ? <ArrowUp size={12} className="me-1" /> : <ArrowDown size={12} className="me-1" />}
                {trendValue}
              </div>
            )}
          </div>
          {subtitle && !loading && (
            <small className="text-muted d-block mt-2" style={{ fontSize: "11px" }}>
              <span className="fw-medium">{subtitle}</span>
            </small>
          )}
          {secondaryValue !== undefined && !loading && (
            <div className="mt-2 pt-2 border-top d-flex justify-content-between align-items-center">
              <small className="text-muted" style={{ fontSize: "10px" }}>{secondaryLabel}</small>
              <Badge bg="light" text="dark" className="rounded-pill" style={{ fontSize: "11px", fontWeight: 600 }}>
                {secondaryValue}
              </Badge>
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };

  // RENDU DE LA CARTE TAXE
  const renderTaxCard = (taxStats, config) => {
    if (!taxStats) return null;

    const hasTaxeId = taxStats.taxe_id != null && taxStats.taxe_id !== "";
    const hasTaxeIds = Array.isArray(taxStats.taxe_ids) && taxStats.taxe_ids.length > 0;

    if (!hasTaxeId && !hasTaxeIds) return null;

    const { color, bgColor, icon: Icon, title, series, options } = config;

    const displayName = taxStats.taxe_nom
      || (Array.isArray(taxStats.taxes_noms) && taxStats.taxes_noms.length > 0
        ? taxStats.taxes_noms.join(" / ")
        : title);

    const isMultiTax = Array.isArray(taxStats.taxe_ids) && taxStats.taxe_ids.length > 1;

    const totalAttendu = parseFloat(taxStats.total_attendu) || 0;
    const totalCollecte = parseFloat(taxStats.total_collecte) || 0;
    const resteAPayer = Math.max(0, totalAttendu - totalCollecte);
    const tauxRecouvrement = parseFloat(taxStats.taux_recouvrement) || 0;
    const nombreContribuables = parseInt(taxStats.nombre_contribuables) || 0;
    const nombrePaiements = parseInt(taxStats.nombre_paiements) || 0;
    const echeancesPayees = parseInt(taxStats.echeances_payees) || 0;
    const echeancesPartiel = parseInt(taxStats.echeances_partiel) || 0;
    const echeancesImpayees = parseInt(taxStats.echeances_impayees) || 0;
    const echeancesTotal = parseInt(taxStats.echeances_total) || 0;

    return (
      <Row className="g-4 mb-4">
        <Col lg={12}>
          <Card className="border-0 shadow-sm"
            style={{
              borderRadius: "16px",
              background: `linear-gradient(135deg, ${bgColor} 0%, ${bgColor} 100%)`,
              borderLeft: `4px solid ${color}`,
              overflow: "hidden",
            }}>
            <Card.Header className="bg-transparent border-bottom py-3 px-4">
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                <div className="d-flex align-items-center">
                  <div className="rounded-3 p-2 me-3" style={{ backgroundColor: `${color}20` }}>
                    <Icon size={22} color={color} />
                  </div>
                  <div>
                    <Card.Title as="h6" className="mb-0 fw-bold" style={{ fontSize: "16px", color: "#0f172a" }}>
                      {displayName}
                      <Badge bg="primary" className="ms-2 rounded-pill"
                        style={{ fontSize: "10px", backgroundColor: `${color} !important` }}>
                        <Lightning className="me-1" size={10} /> En cours
                      </Badge>
                      {isMultiTax && (
                        <Badge bg="secondary" className="ms-1 rounded-pill" style={{ fontSize: "9px" }}>
                          {taxStats.taxe_ids.length} variantes
                        </Badge>
                      )}
                    </Card.Title>
                    <div className="d-flex align-items-center gap-3 flex-wrap">
                      <small className="text-muted" style={{ fontSize: "11px" }}>
                        <Calendar size={12} className="me-1" />
                        Suivi analytique • {taxStats.periode || timeRange}
                      </small>
                    </div>
                  </div>
                </div>
                <div className="d-flex gap-2 flex-wrap">
                  <Badge bg="primary" className="rounded-pill px-3 py-2"
                    style={{ fontSize: "11px", backgroundColor: `${color} !important` }}>
                    <Icon className="me-1" size={12} />
                    {nombreContribuables} contribuables
                  </Badge>
                  <Badge bg={tauxRecouvrement >= 80 ? "success" : "warning"}
                    className="rounded-pill px-3 py-2" style={{ fontSize: "11px" }}>
                    {tauxRecouvrement >= 80 ? <CheckCircle className="me-1" size={12} /> : <ExclamationCircle className="me-1" size={12} />}
                    Taux: {tauxRecouvrement}%
                  </Badge>
                </div>
              </div>
            </Card.Header>

            <Card.Body className="p-4">
              <Row className="g-3 mb-4">
                <Col lg={3} md={6}>
                  <div className="p-3 bg-white rounded-3 shadow-sm h-100 border-start border-3" style={{ borderColor: color }}>
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <small className="text-muted d-block" style={{ fontSize: "10px", fontWeight: 600 }}>
                          <Bullseye className="me-1" size={12} /> Total attendu
                        </small>
                        <h5 className="mb-0 fw-bold" style={{ fontSize: "18px", color }}>
                          {formatCurrency(totalAttendu)}
                        </h5>
                      </div>
                      <div className="rounded-circle d-flex align-items-center justify-content-center"
                        style={{ width: "40px", height: "40px", backgroundColor: `${color}15` }}>
                        <Receipt size={18} color={color} />
                      </div>
                    </div>
                  </div>
                </Col>

                <Col lg={3} md={6}>
                  <div className="p-3 bg-white rounded-3 shadow-sm h-100 border-start border-3" style={{ borderColor: "#22c55e" }}>
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <small className="text-muted d-block" style={{ fontSize: "10px", fontWeight: 600 }}>
                          <Cash className="me-1" size={12} /> Total collecté
                        </small>
                        <h5 className="mb-0 fw-bold" style={{ fontSize: "18px", color: "#22c55e" }}>
                          {formatCurrency(totalCollecte)}
                        </h5>
                      </div>
                      <div className="rounded-circle d-flex align-items-center justify-content-center"
                        style={{ width: "40px", height: "40px", backgroundColor: "rgba(34,197,94,0.1)" }}>
                        <Cash size={18} color="#22c55e" />
                      </div>
                    </div>
                  </div>
                </Col>

                <Col lg={3} md={6}>
                  <div className="p-3 bg-white rounded-3 shadow-sm h-100 border-start border-3" style={{ borderColor: "#f59e0b" }}>
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <small className="text-muted d-block" style={{ fontSize: "10px", fontWeight: 600 }}>
                          <ArrowDown className="me-1" size={12} /> Reste à payer
                        </small>
                        <h5 className="mb-0 fw-bold" style={{ fontSize: "18px", color: "#d97706" }}>
                          {formatCurrency(resteAPayer)}
                        </h5>
                      </div>
                      <div className="rounded-circle d-flex align-items-center justify-content-center"
                        style={{ width: "40px", height: "40px", backgroundColor: "rgba(245,158,11,0.1)" }}>
                        <Clock size={18} color="#f59e0b" />
                      </div>
                    </div>
                  </div>
                </Col>

                <Col lg={3} md={6}>
                  <div className="p-3 bg-white rounded-3 shadow-sm h-100 border-start border-3" style={{ borderColor: "#06b6d4" }}>
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <small className="text-muted d-block" style={{ fontSize: "10px", fontWeight: 600 }}>
                          <GraphUp className="me-1" size={12} /> Taux de recouvrement
                        </small>
                        <h5 className="mb-0 fw-bold" style={{ fontSize: "18px", color: tauxRecouvrement >= 80 ? "#22c55e" : "#f59e0b" }}>
                          {tauxRecouvrement}%
                        </h5>
                      </div>
                      <div className="rounded-circle d-flex align-items-center justify-content-center"
                        style={{ width: "40px", height: "40px", backgroundColor: tauxRecouvrement >= 80 ? "rgba(34,197,94,0.1)" : "rgba(245,158,11,0.1)" }}>
                        <GraphUp size={18} color={tauxRecouvrement >= 80 ? "#22c55e" : "#f59e0b"} />
                      </div>
                    </div>
                    <ProgressBar now={tauxRecouvrement}
                      variant={tauxRecouvrement >= 80 ? "success" : tauxRecouvrement >= 50 ? "warning" : "danger"}
                      style={{ height: "6px", borderRadius: "3px" }} className="mt-2" />
                  </div>
                </Col>
              </Row>

              <Row className="g-3 mb-4">
                <Col lg={7}>
                  <div className="bg-white rounded-3 shadow-sm p-3 h-100">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div>
                        <small className="text-muted d-block fw-bold" style={{ fontSize: "11px" }}>
                          <GraphUp className="me-1" size={14} /> Évolution des collectes
                        </small>
                      </div>
                      <Badge bg="light" text="dark" className="rounded-pill px-2 py-1" style={{ fontSize: "9px" }}>
                        {taxStats.evolution?.length || 0} mois
                      </Badge>
                    </div>
                    {taxStats.evolution && taxStats.evolution.length > 0 ? (
                      <ReactApexChart options={options} series={series} type="area" height={180} />
                    ) : (
                      <div className="text-center py-3">
                        <ExclamationCircle size={32} className="text-muted opacity-25" />
                        <p className="text-muted small">Aucune donnée d'évolution disponible</p>
                      </div>
                    )}
                  </div>
                </Col>

                <Col lg={5}>
                  <div className="bg-white rounded-3 shadow-sm p-3 h-100">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div>
                        <small className="text-muted d-block fw-bold" style={{ fontSize: "11px" }}>
                          <PieChart className="me-1" size={14} /> Répartition par marché
                        </small>
                      </div>
                      <Badge bg="light" text="dark" className="rounded-pill px-2 py-1" style={{ fontSize: "9px" }}>
                        {taxStats.par_marche?.length || 0} marchés
                      </Badge>
                    </div>
                    {taxStats.par_marche && taxStats.par_marche.length > 0 ? (
                      <div className="mt-1">
                        {taxStats.par_marche.slice(0, 5).map((item, idx) => {
                          const percentage = parseFloat(item.taux_recouvrement) || 0;
                          const barColor = percentage >= 80 ? "success" : percentage >= 50 ? "warning" : "danger";
                          const collecteVal = parseFloat(item.collecte) || 0;
                          return (
                            <div key={idx} className="mb-2">
                              <div className="d-flex justify-content-between align-items-center">
                                <div className="d-flex align-items-center">
                                  <div className="rounded-circle me-2"
                                    style={{
                                      width: "8px", height: "8px",
                                      backgroundColor: [color, "#22c55e", "#0d6efd", "#f59e0b", "#ef4444"][idx % 5],
                                    }} />
                                  <span style={{ fontSize: "12px", fontWeight: 500, color: "#0f172a" }}>
                                    {item.marche_nom}
                                  </span>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                  <span className="text-success fw-bold" style={{ fontSize: "11px" }}>
                                    {formatCurrency(collecteVal)}
                                  </span>
                                  <Badge bg={barColor} className="rounded-pill" style={{ fontSize: "9px", minWidth: "40px" }}>
                                    {percentage}%
                                  </Badge>
                                </div>
                              </div>
                              <ProgressBar now={Math.min(percentage, 100)} variant={barColor}
                                style={{ height: "4px", borderRadius: "2px" }} />
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-3">
                        <ExclamationCircle size={32} className="text-muted opacity-25" />
                        <p className="text-muted small">Aucune répartition disponible</p>
                      </div>
                    )}
                  </div>
                </Col>
              </Row>

              <Row className="g-3">
                <Col lg={4}>
                  <div className="bg-white rounded-3 shadow-sm p-3 h-100">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <Award size={16} color={color} />
                      <small className="text-muted fw-bold" style={{ fontSize: "11px" }}>Performance globale</small>
                    </div>
                    <div className="d-flex align-items-center gap-3">
                      <div className="text-center">
                        <h6 className="mb-0 fw-bold" style={{ fontSize: "20px", color: tauxRecouvrement >= 80 ? "#22c55e" : "#f59e0b" }}>
                          {tauxRecouvrement}%
                        </h6>
                        <small className="text-muted" style={{ fontSize: "9px" }}>Taux global</small>
                      </div>
                      <div className="flex-grow-1">
                        <ProgressBar now={tauxRecouvrement}
                          variant={tauxRecouvrement >= 80 ? "success" : "warning"}
                          style={{ height: "8px", borderRadius: "4px" }} />
                        <div className="d-flex justify-content-between mt-1">
                          <small className="text-muted" style={{ fontSize: "9px" }}>{nombrePaiements} paiements</small>
                          <small className="text-muted" style={{ fontSize: "9px" }}>{nombreContribuables} contribuables</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </Col>

                <Col lg={4}>
                  <div className="bg-white rounded-3 shadow-sm p-3 h-100">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <Clock size={16} color={color} />
                      <small className="text-muted fw-bold" style={{ fontSize: "11px" }}>Échéances</small>
                      <Badge bg="secondary" className="rounded-pill ms-auto" style={{ fontSize: "9px" }}>
                        {echeancesTotal} total
                      </Badge>
                    </div>
                    <div className="d-flex justify-content-around">
                      <div className="text-center">
                        <div className="d-flex align-items-center justify-content-center gap-1">
                          <div className="rounded-circle" style={{ width: "8px", height: "8px", backgroundColor: "#22c55e" }} />
                          <h6 className="mb-0 fw-bold text-success" style={{ fontSize: "18px" }}>{echeancesPayees}</h6>
                        </div>
                        <small className="text-muted" style={{ fontSize: "9px" }}>Payées</small>
                      </div>
                      <div className="text-center">
                        <div className="d-flex align-items-center justify-content-center gap-1">
                          <div className="rounded-circle" style={{ width: "8px", height: "8px", backgroundColor: "#f59e0b" }} />
                          <h6 className="mb-0 fw-bold text-warning" style={{ fontSize: "18px" }}>{echeancesPartiel}</h6>
                        </div>
                        <small className="text-muted" style={{ fontSize: "9px" }}>Partielles</small>
                      </div>
                      <div className="text-center">
                        <div className="d-flex align-items-center justify-content-center gap-1">
                          <div className="rounded-circle" style={{ width: "8px", height: "8px", backgroundColor: "#ef4444" }} />
                          <h6 className="mb-0 fw-bold text-danger" style={{ fontSize: "18px" }}>{echeancesImpayees}</h6>
                        </div>
                        <small className="text-muted" style={{ fontSize: "9px" }}>Impayées</small>
                      </div>
                    </div>
                  </div>
                </Col>

                <Col lg={4}>
                  <div className="bg-white rounded-3 shadow-sm p-3 h-100">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <GraphUp size={16} color="#22c55e" />
                      <small className="text-muted fw-bold" style={{ fontSize: "11px" }}>Évolution trimestrielle</small>
                    </div>
                    <div className="d-flex justify-content-around">
                      {(() => {
                        const evolution = taxStats.evolution || [];
                        if (evolution.length >= 3) {
                          const last3 = evolution.slice(-3);
                          const moyenne = (last3.reduce((sum, e) => sum + (parseFloat(e.collecte) || 0), 0) / last3.length).toFixed(0);
                          const firstVal = parseFloat(last3[0]?.collecte) || 0;
                          const lastVal = parseFloat(last3[last3.length - 1]?.collecte) || 0;
                          const variation = firstVal > 0
                            ? (((lastVal - firstVal) / firstVal) * 100).toFixed(1)
                            : (lastVal > 0 ? "100.0" : "0.0");
                          return (
                            <>
                              <div className="text-center">
                                <h6 className="mb-0 fw-bold" style={{ fontSize: "18px", color: "#0f172a" }}>
                                  {formatCurrency(parseFloat(moyenne))}
                                </h6>
                                <small className="text-muted" style={{ fontSize: "9px" }}>Moyenne</small>
                              </div>
                              <div className="text-center">
                                <h6 className="mb-0 fw-bold"
                                  style={{ fontSize: "18px", color: parseFloat(variation) >= 0 ? "#22c55e" : "#ef4444" }}>
                                  {variation}%
                                  {parseFloat(variation) >= 0 ? <ArrowUp size={14} className="ms-1" /> : <ArrowDown size={14} className="ms-1" />}
                                </h6>
                                <small className="text-muted" style={{ fontSize: "9px" }}>Variation</small>
                              </div>
                            </>
                          );
                        }
                        return <div className="text-center py-2"><small className="text-muted">Données insuffisantes</small></div>;
                      })()}
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  };

  useEffect(() => {
    updatePeriod("month");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4" style={{ background: "#f1f5f9", minHeight: "100vh" }}>
        <Container fluid>
          {/* En-tête */}
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
            <div className="d-flex align-items-center gap-3">
              <div className="p-3 rounded-3 d-flex align-items-center justify-content-center"
                style={{
                  background: "linear-gradient(135deg, #4f46e5, #7c3aed)",
                  borderRadius: "16px !important",
                  width: "56px",
                  height: "56px",
                }}>
                <Globe size={28} color="white" />
              </div>
              <div>
                <h4 className="fw-bold mb-0" style={{ color: "#0f172a", fontSize: "22px" }}>
                  Tableau de bord des marchés
                </h4>
                <p className="text-muted mb-0 d-flex align-items-center gap-2" style={{ fontSize: "14px" }}>
                  <Calendar size={14} />
                  Vue d'ensemble de <strong>{loading ? "..." : dashboardData.markets.length || 0}</strong> marchés
                  <span className="text-muted">•</span>
                  {new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                </p>
              </div>
            </div>

            <div className="d-flex flex-column gap-2 mt-3 mt-md-0" style={{ minWidth: "600px" }}>
              <div className="d-flex gap-2 flex-wrap align-items-end justify-content-end">
                <div>
                  <small className="text-muted d-block mb-1 fw-semibold" style={{ fontSize: "11px" }}>Période rapide</small>
                  <ButtonGroup size="sm" className="shadow-sm">
                    <Button
                      variant={dateRange.period === "week" ? "primary" : "outline-primary"}
                      onClick={() => handleTimeRangeChange("week")}
                      disabled={loading}
                      style={{
                        borderRadius: "8px 0 0 8px",
                        borderColor: dateRange.period === "week" ? "#4f46e5" : "#cbd5e1",
                        backgroundColor: dateRange.period === "week" ? "#4f46e5" : "transparent",
                        color: dateRange.period === "week" ? "white" : "#475569",
                      }}>
                      7 jours
                    </Button>
                    <Button
                      variant={dateRange.period === "month" ? "primary" : "outline-primary"}
                      onClick={() => handleTimeRangeChange("month")}
                      disabled={loading}
                      style={{
                        borderColor: dateRange.period === "month" ? "#4f46e5" : "#cbd5e1",
                        backgroundColor: dateRange.period === "month" ? "#4f46e5" : "transparent",
                        color: dateRange.period === "month" ? "white" : "#475569",
                      }}>
                      30 jours
                    </Button>
                    <Button
                      variant={dateRange.period === "year" ? "primary" : "outline-primary"}
                      onClick={() => handleTimeRangeChange("year")}
                      disabled={loading}
                      style={{
                        borderRadius: "0 8px 8px 0",
                        borderColor: dateRange.period === "year" ? "#4f46e5" : "#cbd5e1",
                        backgroundColor: dateRange.period === "year" ? "#4f46e5" : "transparent",
                        color: dateRange.period === "year" ? "white" : "#475569",
                      }}>
                      1 an
                    </Button>
                  </ButtonGroup>
                </div>

                <div>
                  <small className="text-muted d-block mb-1 fw-semibold" style={{ fontSize: "11px" }}>Date de début</small>
                  <Form.Control type="date" size="sm"
                    value={dateRange.startDate}
                    onChange={(e) => setDateRange((prev) => ({ ...prev, startDate: e.target.value, period: "custom" }))}
                    disabled={loading}
                    style={{ borderRadius: "8px", borderColor: "#cbd5e1", width: "150px" }} />
                </div>

                <div>
                  <small className="text-muted d-block mb-1 fw-semibold" style={{ fontSize: "11px" }}>Date de fin</small>
                  <Form.Control type="date" size="sm"
                    value={dateRange.endDate}
                    onChange={(e) => setDateRange((prev) => ({ ...prev, endDate: e.target.value, period: "custom" }))}
                    disabled={loading}
                    style={{ borderRadius: "8px", borderColor: "#cbd5e1", width: "150px" }} />
                </div>

                <div>
                  <small className="text-muted d-block mb-1 fw-semibold" style={{ fontSize: "11px" }}>&nbsp;</small>
                  <Button variant="outline-secondary" size="sm" onClick={resetDateRange}
                    disabled={loading} style={{ borderRadius: "8px", borderColor: "#cbd5e1" }}>
                    <ClockHistory size={14} className="me-1" /> Réinitialiser
                  </Button>
                </div>
              </div>

              <div className="d-flex gap-2 flex-wrap justify-content-end align-items-center">
                {/* ✅ NOUVEAU : Bouton Export par marché */}
                <Button
                  variant="primary"
                  size="sm"
                  className="shadow-sm"
                  onClick={openMarketExportModal}
                  disabled={loading}
                  style={{ borderRadius: "8px" }}
                >
                  <FileEarmarkBarGraph size={14} className="me-1" />
                  Export par marché
                </Button>

                <Dropdown>
                  <Dropdown.Toggle variant="outline-secondary" size="sm" className="shadow-sm"
                    disabled={exportLoading || loading} style={{ borderRadius: "8px", borderColor: "#cbd5e1" }}>
                    {exportLoading ? <Spinner animation="border" size="sm" /> : <Download size={14} className="me-1" />}
                    Exporter tout
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item onClick={handleExportPDF} disabled={exportLoading || loading}>
                      <FilePdf className="me-2 text-danger" size={16} /> PDF
                    </Dropdown.Item>
                    <Dropdown.Item onClick={handleExportExcel} disabled={exportLoading || loading}>
                      <FileEarmarkExcel className="me-2 text-success" size={16} /> Excel
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>

                <Button variant="outline-secondary" size="sm" className="shadow-sm"
                  onClick={() => loadDashboardData(true)} disabled={refreshLoading || loading}
                  style={{ borderRadius: "8px", borderColor: "#cbd5e1" }}>
                  {refreshLoading ? <Spinner animation="border" size="sm" /> : <ClockHistory size={16} />}
                </Button>
              </div>

              {(dateRange.startDate || dateRange.endDate) && (
                <div className="d-flex justify-content-end">
                  <Badge bg="info" className="px-3 py-2" style={{ fontSize: "11px" }}>
                    <Calendar size={12} className="me-1" />
                    Période : {dateRange.startDate || "Début"} → {dateRange.endDate || "Aujourd'hui"}
                  </Badge>
                </div>
              )}
            </div>
          </div>

          {error && (
            <Alert variant="danger" className="border-0 shadow-sm d-flex align-items-center justify-content-between"
              style={{ borderRadius: "12px" }}>
              <div className="d-flex align-items-center">
                <ExclamationCircle size={24} className="me-2" />
                <span className="fw-medium">{error}</span>
              </div>
              <Button variant="outline-danger" size="sm" onClick={() => loadDashboardData(true)} style={{ borderRadius: "8px" }}>
                <Repeat className="me-1" size={14} /> Réessayer
              </Button>
            </Alert>
          )}

          {/* Alerte : Aucune donnée sur la période */}
          {!loading && !stats.hasData && (
            <Alert variant="info"
              className="border-0 shadow-sm d-flex align-items-center justify-content-between flex-wrap gap-2"
              style={{ borderRadius: "12px" }}>
              <div className="d-flex align-items-center">
                <Info size={22} className="me-2" />
                <div>
                  <strong>Aucune activité sur cette période.</strong>
                  <div className="small text-muted">
                    {stats.derniereActivite
                      ? `Dernière activité enregistrée : ${formatDate(stats.derniereActivite)}`
                      : "Aucune activité enregistrée"}
                  </div>
                </div>
              </div>
              {stats.derniereActivite && (
                <Button variant="info" size="sm" style={{ borderRadius: "8px" }}
                  onClick={goToLastActivity}>
                  <Calendar size={14} className="me-1" /> Voir cette journée
                </Button>
              )}
            </Alert>
          )}

          {/* Statistiques Globales */}
          <Row className="g-3 mb-4">
            {loading ? (
              <>
                <Col md={6} lg={3}><SkeletonStatCard /></Col>
                <Col md={6} lg={3}><SkeletonStatCard /></Col>
                <Col md={6} lg={3}><SkeletonStatCard /></Col>
                <Col md={6} lg={3}><SkeletonStatCard /></Col>
              </>
            ) : (
              <>
                <Col md={6} lg={3}>
                  <StatCard
                    title="Total Marchés"
                    value={stats.totalMarkets}
                    icon={Shop}
                    color="primary"
                    subtitle={`${dashboardData.markets.length} marchés actifs`}
                  />
                </Col>

                <Col md={6} lg={3}>
                  <StatCard
                    title="Contribuables"
                    value={stats.totalContribuables}
                    icon={People}
                    color="info"
                    subtitle={`${stats.contribuablesActifsPeriode} actifs • ${stats.contribuablesEnrolesPeriode} enrôlés sur la période`}
                    trend={stats.contribuablesEnrolesPeriode > 0 ? "up" : null}
                    trendValue={stats.contribuablesEnrolesPeriode > 0 ? `+${stats.contribuablesEnrolesPeriode}` : null}
                    secondaryValue={`+${stats.contribuablesEnrolesPeriode} enrôlés`}
                    secondaryLabel="Sur la période"
                  />
                </Col>

                <Col md={6} lg={3}>
                  <StatCard
                    title="Collectes Totales"
                    value={formatCurrency(stats.totalCollecte)}
                    icon={Cash}
                    color="success"
                    subtitle={`Sur ${formatNumberWithDecimals(dashboardData.globalStats?.total_attendu || 0)} ${defaultCurrency} attendus`}
                  />
                </Col>
                <Col md={6} lg={3}>
                  <StatCard
                    title="Taux de Recouvrement"
                    value={`${stats.tauxRecouvrement}%`}
                    icon={GraphUp}
                    color={stats.tauxRecouvrement >= 80 ? "success" : "warning"}
                    subtitle={stats.tauxRecouvrement >= 80 ? "✅ Objectif atteint" : "⚠️ En dessous de l'objectif"}
                  />
                </Col>
              </>
            )}
          </Row>

          {/* Cartes Taxes */}
          {!loading && renderTaxCard(dashboardData.salubriteStats, {
            color: "#ef4444", bgColor: "#fef2f2", icon: Trash, title: "Taxe Salubrité",
            series: salubriteSeries, options: salubriteEvolutionOptions,
          })}

          {!loading && renderTaxCard(dashboardData.loyerStats, {
            color: "#0d6efd", bgColor: "#eff6ff", icon: House, title: "Taxe Loyers",
            series: loyerSeries, options: loyerEvolutionOptions,
          })}

          {!loading && renderTaxCard(dashboardData.etalageStats, {
            color: "#8b5cf6", bgColor: "#f5f3ff", icon: Grid, title: "Taxe Étalage",
            series: etalageSeries, options: etalageEvolutionOptions,
          })}

          {/* Comparaison des Marchés */}
          <Row className="g-4 mb-4">
            <Col lg={7}>
              <Card className="border-0 shadow-sm h-100" style={{ borderRadius: "16px" }}>
                <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center py-3">
                  <div>
                    <Card.Title as="h6" className="mb-0 fw-semibold" style={{ fontSize: "14px", color: "#0f172a" }}>
                      <BarChart className="me-2" size={18} color="#4f46e5" />
                      Comparaison des Marchés
                    </Card.Title>
                    <small className="text-muted" style={{ fontSize: "11px" }}>Performance par marché</small>
                  </div>
                  {dashboardData.markets.length > 0 && (
                    <Dropdown>
                      <Dropdown.Toggle variant="outline-secondary" size="sm"
                        style={{ borderRadius: "8px", borderColor: "#cbd5e1" }}>
                        {selectedMarket === "all" ? "Tous les marchés" : dashboardData.markets.find((m) => m.id_marche === selectedMarket)?.nom_marche || "Sélectionner"}
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item onClick={() => setSelectedMarket("all")}>Tous les marchés</Dropdown.Item>
                        {dashboardData.markets.map((m) => (
                          <Dropdown.Item key={m.id_marche} onClick={() => setSelectedMarket(m.id_marche)}>
                            {m.nom_marche}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  )}
                </Card.Header>
                <Card.Body>
                  {loading ? (
                    <div className="text-center py-5">
                      <Spinner animation="border" variant="primary" />
                      <p className="mt-2 text-muted small">Chargement...</p>
                    </div>
                  ) : dashboardData.marketComparison.length > 0 ? (
                    <ReactApexChart
                      options={comparisonChartOptions}
                      series={[
                        { name: "Collecté", data: dashboardData.marketComparison.map((m) => m.collecte) },
                        { name: "Objectif", data: dashboardData.marketComparison.map((m) => m.objectif) },
                      ]}
                      type="bar"
                      height={450}
                    />
                  ) : (
                    <div className="text-center py-5">
                      <ExclamationCircle size={48} className="text-muted opacity-25 mb-3" />
                      <p className="text-muted">Aucune donnée disponible pour la comparaison</p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col lg={5}>
              <Row className="g-4">
                <Col md={12}>
                  <Card className="border-0 shadow-sm h-100" style={{ borderRadius: "16px" }}>
                    <Card.Header className="bg-white border-bottom py-3">
                      <Card.Title as="h6" className="mb-0 fw-semibold" style={{ fontSize: "14px", color: "#0f172a" }}>
                        <PieChart className="me-2" size={18} color="#4f46e5" />
                        Répartition des Taxes
                      </Card.Title>
                    </Card.Header>
                    <Card.Body>
                      {loading ? (
                        <div className="text-center py-4"><Spinner animation="border" variant="primary" /></div>
                      ) : dashboardData.taxDistribution.length > 0 ? (
                        <ReactApexChart options={taxDistributionOptions}
                          series={dashboardData.taxDistribution.map((t) => t.montant)}
                          type="donut" height={280} />
                      ) : (
                        <div className="text-center py-4">
                          <ExclamationCircle size={40} className="text-muted opacity-25 mb-2" />
                          <p className="text-muted small">Aucune donnée fiscale disponible</p>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={12}>
                  <Card className="border-0 shadow-sm h-100" style={{ borderRadius: "16px" }}>
                    <Card.Header className="bg-white border-bottom py-3">
                      <Card.Title as="h6" className="mb-0 fw-semibold" style={{ fontSize: "14px", color: "#0f172a" }}>
                        <Activity className="me-2" size={18} color="#4f46e5" />
                        Évolution Mensuelle
                      </Card.Title>
                    </Card.Header>
                    <Card.Body>
                      {loading ? (
                        <div className="text-center py-4"><Spinner animation="border" variant="primary" /></div>
                      ) : dashboardData.monthlyEvolution.length > 0 ? (
                        <ReactApexChart options={evolutionChartOptions}
                          series={[{ name: "Collectes", data: dashboardData.monthlyEvolution.map((e) => e.collecte) }]}
                          type="area" height={250} />
                      ) : (
                        <div className="text-center py-4">
                          <ExclamationCircle size={40} className="text-muted opacity-25 mb-2" />
                          <p className="text-muted small">Aucune donnée d'évolution</p>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Col>
          </Row>

          {/* Tableau détaillé */}
          <Row className="g-4 mb-4">
            <Col lg={12}>
              <Card className="border-0 shadow-sm" style={{ borderRadius: "16px" }}>
                <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center py-3">
                  <div>
                    <Card.Title as="h6" className="mb-0 fw-semibold" style={{ fontSize: "14px", color: "#0f172a" }}>
                      <Layers className="me-2" size={18} color="#4f46e5" />
                      Détail des Marchés
                    </Card.Title>
                  </div>
                  <Badge bg="primary" className="rounded-pill px-3 py-2" style={{ fontSize: "11px" }}>
                    {dashboardData.marketComparison.length} marchés
                  </Badge>
                </Card.Header>
                <Card.Body className="p-0">
                  {loading ? (
                    <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
                  ) : (
                    <div style={{ overflowX: "auto" }}>
                      <Table hover responsive className="mb-0" style={{ fontSize: "13px" }}>
                        <thead className="bg-light">
                          <tr>
                            <th className="py-3" style={{ width: "50px", color: "#475569" }}>#</th>
                            <th className="py-3" style={{ color: "#475569" }}>Marché</th>
                            <th className="py-3 text-end" style={{ color: "#475569" }}>Contribuables</th>
                            <th className="py-3 text-end" style={{ color: "#475569" }}>Enrôlés période</th>
                            <th className="py-3 text-end" style={{ color: "#475569" }}>Taux d'occupation</th>
                            <th className="py-3 text-end" style={{ color: "#475569" }}>Collecté</th>
                            <th className="py-3 text-end" style={{ color: "#475569" }}>Objectif</th>
                            <th className="py-3 text-end" style={{ color: "#475569" }}>Performance</th>
                            <th className="py-3 text-center" style={{ color: "#475569" }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dashboardData.marketComparison.map((market, idx) => (
                            <tr key={idx} className="align-middle">
                              <td className="fw-bold" style={{ color: "#64748b" }}>{idx + 1}</td>
                              <td>
                                <div className="d-flex align-items-center">
                                  <Shop className="me-2" size={14} color="#4f46e5" />
                                  <span className="fw-medium" style={{ color: "#0f172a" }}>{market.nom}</span>
                                </div>
                              </td>
                              <td className="text-end" style={{ color: "#475569" }}>
                                {market.total_contribuables || market.nombre_commercants || 0}
                              </td>
                              <td className="text-end">
                                {market.contribuables_enroles_periode > 0 ? (
                                  <Badge bg="primary" className="rounded-pill" style={{ fontSize: "10px" }}>
                                    <PersonPlus size={10} className="me-1" />
                                    +{market.contribuables_enroles_periode}
                                  </Badge>
                                ) : (
                                  <span className="text-muted" style={{ fontSize: "11px" }}>0</span>
                                )}
                              </td>
                              <td className="text-end">
                                <Badge bg={market.taux_occupation >= 80 ? "success" : "warning"}
                                  className="rounded-pill" style={{ fontSize: "10px" }}>
                                  {market.taux_occupation || 0}%
                                </Badge>
                              </td>
                              <td className="text-end text-success fw-bold">{formatCurrency(market.collecte)}</td>
                              <td className="text-end text-muted">{formatCurrency(market.objectif)}</td>
                              <td className="text-end">
                                <div className="d-flex align-items-center justify-content-end">
                                  <ProgressBar now={market.performance}
                                    variant={market.performance >= 100 ? "success" : "warning"}
                                    style={{ width: "80px", height: "5px" }} className="me-2" />
                                  <span className={`fw-bold ${market.performance >= 100 ? "text-success" : "text-warning"}`}
                                    style={{ fontSize: "12px" }}>
                                    {market.performance}%
                                  </span>
                                </div>
                              </td>
                              <td className="text-center">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  className="rounded-pill"
                                  onClick={() => {
                                    setSelectedMarketForExport(market.id);
                                    setUseMainPeriod(true);
                                    setMarketExportPeriod({
                                      startDate: dateRange.startDate,
                                      endDate: dateRange.endDate,
                                      period: dateRange.period,
                                    });
                                    setShowMarketExportModal(true);
                                  }}
                                  title="Voir les stats détaillées"
                                >
                                  <BoxArrowUpRight size={12} />
                                </Button>
                              </td>
                            </tr>
                          ))}
                          {dashboardData.marketComparison.length === 0 && (
                            <tr>
                              <td colSpan="9" className="text-center py-4 text-muted">
                                <ExclamationCircle size={24} className="me-2" />
                                Aucun marché disponible
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Activités récentes */}
          <Row>
            <Col lg={12}>
              <Card className="border-0 shadow-sm" style={{ borderRadius: "16px" }}>
                <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center py-3">
                  <div>
                    <Card.Title as="h6" className="mb-0 fw-semibold" style={{ fontSize: "14px", color: "#0f172a" }}>
                      <Clock className="me-2" size={18} color="#4f46e5" />
                      Activités Récentes
                    </Card.Title>
                  </div>
                  <Button variant="outline-primary" size="sm" className="rounded-pill"
                    style={{ fontSize: "12px", borderColor: "#4f46e5", color: "#4f46e5" }}>
                    <Eye className="me-1" size={14} /> Voir tout
                  </Button>
                </Card.Header>
                <Card.Body className="p-0">
                  {loading ? (
                    <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
                  ) : dashboardData.recentActivities.length > 0 ? (
                    <div style={{ overflowX: "auto" }}>
                      <Table hover responsive className="mb-0" style={{ fontSize: "13px" }}>
                        <thead className="bg-light">
                          <tr>
                            <th className="py-3" style={{ color: "#475569" }}>Date</th>
                            <th className="py-3" style={{ color: "#475569" }}>Marché</th>
                            <th className="py-3" style={{ color: "#475569" }}>Contribuable</th>
                            <th className="py-3 text-end" style={{ color: "#475569" }}>Montant</th>
                            <th className="py-3 text-center" style={{ color: "#475569" }}>Statut</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dashboardData.recentActivities.slice(0, 10).map((activity, idx) => (
                            <tr key={idx} className="align-middle">
                              <td style={{ color: "#475569" }}>{formatDate(activity.created_at)}</td>
                              <td>
                                <Badge bg="info" className="rounded-pill px-3 py-1" style={{ fontSize: "10px" }}>
                                  <Shop className="me-1" size={10} />
                                  {activity.marche || "-"}
                                </Badge>
                              </td>
                              <td className="fw-medium" style={{ color: "#0f172a" }}>{activity.contribuable}</td>
                              <td className="text-end text-success fw-bold">{formatCurrency(activity.montant)}</td>
                              <td className="text-center">
                                <Badge bg="success" className="rounded-pill px-3 py-1" style={{ fontSize: "10px" }}>
                                  <CheckCircle className="me-1" size={10} /> Payé
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-center py-4 text-muted">
                      <Clock size={32} className="mb-2 opacity-25" />
                      <p>Aucune activité récente</p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* ✅ MODAL : Export par marché */}
          <Modal
            show={showMarketExportModal}
            onHide={closeMarketExportModal}
            size="xl"
            centered
            backdrop="static"
            scrollable
          >
            <Modal.Header closeButton style={{ borderBottom: "2px solid #e2e8f0" }}>
              <Modal.Title className="d-flex align-items-center" style={{ fontSize: "16px", fontWeight: 700, color: "#0f172a" }}>
                <FileEarmarkBarGraph className="me-2" size={22} color="#4f46e5" />
                Export détaillé par marché
              </Modal.Title>
            </Modal.Header>

            <Modal.Body style={{ padding: "20px" }}>
              {/* Étape 1 : Filtres */}
              <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: "12px", background: "#f8fafc" }}>
                <Card.Body className="p-3">
                  <h6 className="fw-bold mb-3" style={{ color: "#4f46e5", fontSize: "13px" }}>
                    <Calendar size={14} className="me-1" /> ÉTAPE 1 : SÉLECTION DU MARCHÉ ET DE LA PÉRIODE
                  </h6>

                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Label className="fw-semibold mb-1" style={{ fontSize: "12px" }}>
                        <Shop size={12} className="me-1 text-primary" />
                        Marché à analyser
                      </Form.Label>
                      <Form.Select
                        value={selectedMarketForExport}
                        onChange={(e) => {
                          setSelectedMarketForExport(e.target.value);
                          setMarketExportData(null);
                          setMarketExportError(null);
                        }}
                        style={{ borderRadius: "8px" }}
                      >
                        <option value="">-- Choisir un marché --</option>
                        {dashboardData.markets.map((m) => (
                          <option key={m.id_marche} value={m.id_marche}>
                            {m.nom_marche} ({m.code_marche})
                          </option>
                        ))}
                      </Form.Select>
                    </Col>

                    <Col md={6}>
                      <Form.Label className="fw-semibold mb-1" style={{ fontSize: "12px" }}>
                        <Calendar size={12} className="me-1 text-primary" />
                        Période d'analyse
                      </Form.Label>
                      <div className="d-flex gap-2 flex-wrap">
                        <Button
                          size="sm"
                          variant={useMainPeriod ? "primary" : "outline-primary"}
                          onClick={handleUseMainPeriod}
                          style={{ borderRadius: "8px", flex: "1 1 auto" }}
                        >
                          <CheckCircle size={12} className="me-1" />
                          Période actuelle ({dateRange.startDate || "?"} → {dateRange.endDate || "?"})
                        </Button>
                      </div>

                  
                    </Col>
                  </Row>

                  <div className="mt-3 text-end">
                    <Button
                      variant="primary"
                      onClick={loadMarketExportStats}
                      disabled={!selectedMarketForExport || marketExportLoading}
                      style={{ borderRadius: "8px" }}
                    >
                      {marketExportLoading ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Chargement...
                        </>
                      ) : (
                        <>
                          <GraphUp size={14} className="me-1" /> Charger les statistiques
                        </>
                      )}
                    </Button>
                  </div>
                </Card.Body>
              </Card>

              {marketExportError && (
                <Alert variant="danger" className="border-0" style={{ borderRadius: "10px" }}>
                  <ExclamationCircle size={18} className="me-2" />
                  {marketExportError}
                </Alert>
              )}

              {/* Étape 2 : Affichage des stats */}
              {marketExportData && (
                <>
                  {/* En-tête du marché */}
                  <Card className="border-0 shadow-sm mb-4"
                    style={{ borderRadius: "14px", background: "linear-gradient(135deg, #f8fafc, #f1f5f9)" }}>
                    <Card.Body className="p-3">
                      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                        <div>
                          <h5 className="fw-bold mb-1" style={{ color: "#0f172a" }}>
                            <Shop className="me-2" size={20} color="#4f46e5" />
                            {marketExportData.marche.nom_marche}
                          </h5>
                          <div className="d-flex gap-3 flex-wrap">
                            <small className="text-muted">
                              <strong>Code :</strong> {marketExportData.marche.code_marche}
                            </small>
                            <small className="text-muted">
                              <strong>Superficie :</strong> {marketExportData.marche.superficie} m²
                            </small>
                            <small className="text-muted">
                              <Calendar size={12} className="me-1" />
                              {marketExportData.date_range.label}
                            </small>
                          </div>
                        </div>
                        <div className="d-flex gap-2">
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={exportMarketPDF}
                            style={{ borderRadius: "8px" }}
                          >
                            <FilePdf size={14} className="me-1" /> PDF
                          </Button>
                          <Button
                            variant="outline-success"
                            size="sm"
                            onClick={exportMarketExcel}
                            style={{ borderRadius: "8px" }}
                          >
                            <FileEarmarkExcel size={14} className="me-1" /> Excel
                          </Button>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>

                  {/* Synthèse globale */}
                  <h6 className="fw-bold mb-2" style={{ color: "#4f46e5", fontSize: "13px" }}>
                    <BarChart size={14} className="me-1" /> SYNTHÈSE GLOBALE
                  </h6>
                  <Row className="g-2 mb-4">
                    <Col md={3} sm={6}>
                      <div className="p-3 bg-white border rounded-3 h-100">
                        <small className="text-muted d-block" style={{ fontSize: "10px" }}>Contribuables</small>
                        <h5 className="fw-bold mb-0" style={{ color: "#4f46e5" }}>
                          {marketExportData.global_stats.total_contribuables || 0}
                        </h5>
                        <small className="text-success" style={{ fontSize: "10px" }}>
                          +{marketExportData.global_stats.contribuables_enroles_periode || 0} enrôlés
                        </small>
                      </div>
                    </Col>
                    <Col md={3} sm={6}>
                      <div className="p-3 bg-white border rounded-3 h-100">
                        <small className="text-muted d-block" style={{ fontSize: "10px" }}>Total prévu</small>
                        <h5 className="fw-bold mb-0" style={{ color: "#0d6efd", fontSize: "16px" }}>
                          {formatCurrency(marketExportData.global_stats.total_attendu)}
                        </h5>
                      </div>
                    </Col>
                    <Col md={3} sm={6}>
                      <div className="p-3 bg-white border rounded-3 h-100">
                        <small className="text-muted d-block" style={{ fontSize: "10px" }}>Total collecté</small>
                        <h5 className="fw-bold mb-0" style={{ color: "#22c55e", fontSize: "16px" }}>
                          {formatCurrency(marketExportData.global_stats.total_collecte)}
                        </h5>
                      </div>
                    </Col>
                    <Col md={3} sm={6}>
                      <div className="p-3 bg-white border rounded-3 h-100">
                        <small className="text-muted d-block" style={{ fontSize: "10px" }}>Taux de recouvrement</small>
                        <h5 className="fw-bold mb-0" style={{
                          color: marketExportData.global_stats.taux_recouvrement >= 80 ? "#22c55e" : "#f59e0b",
                          fontSize: "16px",
                        }}>
                          {marketExportData.global_stats.taux_recouvrement || 0}%
                        </h5>
                      </div>
                    </Col>
                  </Row>

                  {/* Détail par taxe */}
                  <h6 className="fw-bold mb-2" style={{ color: "#4f46e5", fontSize: "13px" }}>
                    <Receipt size={14} className="me-1" /> DÉTAIL PAR TYPE DE TAXE
                  </h6>
                  <div className="table-responsive mb-3">
                    <Table bordered hover className="mb-0" style={{ fontSize: "12px" }}>
                      <thead style={{ background: "#f8fafc" }}>
                        <tr>
                          <th>Type</th>
                          <th>Nom</th>
                          <th className="text-end">Total prévu</th>
                          <th className="text-end">Total collecté</th>
                          <th className="text-end">Reste</th>
                          <th className="text-center">Taux</th>
                          <th className="text-center">Paiements</th>
                          <th className="text-center">Contribuables</th>
                        </tr>
                      </thead>
                      <tbody>
                        {marketExportData.salubrite_stats && marketExportData.salubrite_stats.total_attendu > 0 && (
                          <tr>
                            <td>
                              <Badge bg="danger" className="rounded-pill">
                                <Trash size={10} className="me-1" /> Salubrité
                              </Badge>
                            </td>
                            <td className="fw-medium">{marketExportData.salubrite_stats.taxe_nom}</td>
                            <td className="text-end text-primary fw-bold">
                              {formatCurrency(marketExportData.salubrite_stats.total_attendu)}
                            </td>
                            <td className="text-end text-success fw-bold">
                              {formatCurrency(marketExportData.salubrite_stats.total_collecte)}
                            </td>
                            <td className="text-end text-danger">
                              {formatCurrency(
                                (marketExportData.salubrite_stats.total_attendu || 0) -
                                (marketExportData.salubrite_stats.total_collecte || 0)
                              )}
                            </td>
                            <td className="text-center">
                              <Badge bg={marketExportData.salubrite_stats.taux_recouvrement >= 80 ? "success" : "warning"}>
                                {marketExportData.salubrite_stats.taux_recouvrement}%
                              </Badge>
                            </td>
                            <td className="text-center">{marketExportData.salubrite_stats.nombre_paiements}</td>
                            <td className="text-center">{marketExportData.salubrite_stats.nombre_contribuables}</td>
                          </tr>
                        )}
                        {marketExportData.loyer_stats && marketExportData.loyer_stats.total_attendu > 0 && (
                          <tr>
                            <td>
                              <Badge bg="primary" className="rounded-pill">
                                <House size={10} className="me-1" /> Loyers
                              </Badge>
                            </td>
                            <td className="fw-medium">{marketExportData.loyer_stats.taxe_nom}</td>
                            <td className="text-end text-primary fw-bold">
                              {formatCurrency(marketExportData.loyer_stats.total_attendu)}
                            </td>
                            <td className="text-end text-success fw-bold">
                              {formatCurrency(marketExportData.loyer_stats.total_collecte)}
                            </td>
                            <td className="text-end text-danger">
                              {formatCurrency(
                                (marketExportData.loyer_stats.total_attendu || 0) -
                                (marketExportData.loyer_stats.total_collecte || 0)
                              )}
                            </td>
                            <td className="text-center">
                              <Badge bg={marketExportData.loyer_stats.taux_recouvrement >= 80 ? "success" : "warning"}>
                                {marketExportData.loyer_stats.taux_recouvrement}%
                              </Badge>
                            </td>
                            <td className="text-center">{marketExportData.loyer_stats.nombre_paiements}</td>
                            <td className="text-center">{marketExportData.loyer_stats.nombre_contribuables}</td>
                          </tr>
                        )}
                        {marketExportData.etalage_stats && marketExportData.etalage_stats.total_attendu > 0 && (
                          <tr>
                            <td>
                              <Badge style={{ background: "#8b5cf6" }} className="rounded-pill">
                                <Grid size={10} className="me-1" /> Étalage
                              </Badge>
                            </td>
                            <td className="fw-medium">{marketExportData.etalage_stats.taxe_nom}</td>
                            <td className="text-end text-primary fw-bold">
                              {formatCurrency(marketExportData.etalage_stats.total_attendu)}
                            </td>
                            <td className="text-end text-success fw-bold">
                              {formatCurrency(marketExportData.etalage_stats.total_collecte)}
                            </td>
                            <td className="text-end text-danger">
                              {formatCurrency(
                                (marketExportData.etalage_stats.total_attendu || 0) -
                                (marketExportData.etalage_stats.total_collecte || 0)
                              )}
                            </td>
                            <td className="text-center">
                              <Badge bg={marketExportData.etalage_stats.taux_recouvrement >= 80 ? "success" : "warning"}>
                                {marketExportData.etalage_stats.taux_recouvrement}%
                              </Badge>
                            </td>
                            <td className="text-center">{marketExportData.etalage_stats.nombre_paiements}</td>
                            <td className="text-center">{marketExportData.etalage_stats.nombre_contribuables}</td>
                          </tr>
                        )}
                        {(!marketExportData.salubrite_stats?.total_attendu &&
                          !marketExportData.loyer_stats?.total_attendu &&
                          !marketExportData.etalage_stats?.total_attendu) && (
                          <tr>
                            <td colSpan="8" className="text-center text-muted py-3">
                              <Info size={20} className="me-2" />
                              Aucune taxe enregistrée sur cette période
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>

                  {/* Paiements par taxe */}
                  {marketExportData.paiements_par_taxe && marketExportData.paiements_par_taxe.length > 0 && (
                    <>
                      <h6 className="fw-bold mb-2 mt-4" style={{ color: "#4f46e5", fontSize: "13px" }}>
                        <Cash size={14} className="me-1" /> DÉTAIL DES PAIEMENTS PAR TAXE
                      </h6>
                      <div className="table-responsive">
                        <Table hover className="mb-0" style={{ fontSize: "12px" }}>
                          <thead style={{ background: "#f8fafc" }}>
                            <tr>
                              <th>Taxe</th>
                              <th className="text-center">Contribuables</th>
                              <th className="text-center">Paiements</th>
                              <th className="text-end">Total collecté</th>
                            </tr>
                          </thead>
                          <tbody>
                            {marketExportData.paiements_par_taxe.map((p, idx) => (
                              <tr key={idx}>
                                <td className="fw-medium">{p.taxe_nom}</td>
                                <td className="text-center">
                                  <Badge bg="info" className="rounded-pill">{p.nombre_contribuables}</Badge>
                                </td>
                                <td className="text-center">
                                  <Badge bg="secondary" className="rounded-pill">{p.nombre_paiements}</Badge>
                                </td>
                                <td className="text-end text-success fw-bold">
                                  {formatCurrency(p.total_collecte)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </>
                  )}

                  {/* Dernières transactions du marché */}
                  {marketExportData.recent_activities && marketExportData.recent_activities.length > 0 && (
                    <>
                      <h6 className="fw-bold mb-2 mt-4" style={{ color: "#4f46e5", fontSize: "13px" }}>
                        <Clock size={14} className="me-1" /> DERNIÈRES TRANSACTIONS
                      </h6>
                      <div className="table-responsive">
                        <Table hover className="mb-0" style={{ fontSize: "12px" }}>
                          <thead style={{ background: "#f8fafc" }}>
                            <tr>
                              <th>Date</th>
                              <th>Contribuable</th>
                              <th className="text-end">Montant</th>
                            </tr>
                          </thead>
                          <tbody>
                            {marketExportData.recent_activities.slice(0, 10).map((a, idx) => (
                              <tr key={idx}>
                                <td>{formatDate(a.created_at)}</td>
                                <td className="fw-medium">{a.contribuable}</td>
                                <td className="text-end text-success fw-bold">{formatCurrency(a.montant)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </>
                  )}
                </>
              )}
            </Modal.Body>

            <Modal.Footer style={{ borderTop: "2px solid #e2e8f0" }}>
              <Button variant="outline-secondary" onClick={closeMarketExportModal} style={{ borderRadius: "8px" }}>
                <XCircle size={14} className="me-1" /> Fermer
              </Button>
            </Modal.Footer>
          </Modal>

          <Footer />
        </Container>
      </div>

      <style>{`
        .transition-card { transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); border-radius: 16px; overflow: hidden; }
        .transition-card:hover { transform: translateY(-6px); box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08) !important; }
        .table > :not(caption) > * > * { padding: 10px 14px; }
        .table-hover tbody tr:hover { background-color: rgba(79, 70, 229, 0.04); }
        .btn-group .btn { border-radius: 6px !important; }
        .btn-group .btn:not(:first-child) { border-top-left-radius: 0 !important; border-bottom-left-radius: 0 !important; }
        .btn-group .btn:not(:last-child) { border-top-right-radius: 0 !important; border-bottom-right-radius: 0 !important; }
        .card { border-radius: 16px !important; }
        .shadow-sm { box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06) !important; }
        .bg-success-subtle { background-color: rgba(34, 197, 94, 0.08); }
        .bg-danger-subtle { background-color: rgba(239, 68, 68, 0.08); }
        .btn-primary { background-color: #4f46e5; border-color: #4f46e5; }
        .btn-primary:hover { background-color: #4338ca; border-color: #4338ca; }
        .btn-outline-primary { border-color: #4f46e5; color: #4f46e5; }
        .btn-outline-primary:hover { background-color: #4f46e5; border-color: #4f46e5; color: white; }
        .text-primary { color: #4f46e5 !important; }
        .bg-primary { background-color: #4f46e5 !important; }
        .badge.bg-primary { background-color: #4f46e5 !important; }
        .badge.bg-success { background-color: #22c55e !important; }
        .badge.bg-warning { background-color: #f59e0b !important; }
        .badge.bg-danger { background-color: #ef4444 !important; }
        .badge.bg-info { background-color: #06b6d4 !important; }
        .badge.bg-secondary { background-color: #94a3b8 !important; }
        .progress-bar { border-radius: 4px; }
        .progress { border-radius: 4px; background-color: #f1f5f9; }
        .table thead th {
          border-bottom: 2px solid #e2e8f0; font-weight: 600; color: #475569;
          font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px;
        }
        .table tbody td { border-bottom: 1px solid #f1f5f9; vertical-align: middle; }
        .table tbody tr:last-child td { border-bottom: none; }
        .dropdown-item:active { background-color: #4f46e5; color: white; }
        .dropdown-item:hover { background-color: #f1f5f9; }
        .btn-outline-secondary { border-color: #cbd5e1; color: #475569; }
        .btn-outline-secondary:hover { background-color: #f1f5f9; border-color: #94a3b8; color: #0f172a; }
        .form-control:focus, .btn:focus, .dropdown-toggle:focus { box-shadow: 0 0 0 2px rgba(79, 70, 229, 0.2); }
        .modal-content { border-radius: 16px; border: none; }
        .modal-header { background: white; }
        .modal-footer { background: white; }
      `}</style>
    </React.Fragment>
  );
}